import React, { useState, useReducer, useEffect, useMemo, useRef } from "react";
import { io } from "socket.io-client";
import {
  LayoutDashboard, ListChecks, Send, HandHelping, Users, BarChart3,
  UserCog, Settings as SettingsIcon, Search, Bell, ChevronDown, Plus,
  MoreHorizontal, X, Check, Clock, AlertTriangle, ArrowUp, ArrowDown,
  Calendar, Tag, MessageSquare, History, Menu, LogOut, Filter, Mail,
  MessageCircle, Zap, ChevronRight, CheckCircle2, PlayCircle,
  CircleDot, ShieldCheck, Repeat, Trash2, Edit3, Copy, Archive, Info,
  Sparkles, Folder, FolderOpen, ListTree, CornerDownRight, Paperclip, Phone, Eye, EyeOff, Download,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart,
  Line, PieChart, Pie, Cell, CartesianGrid, Legend,
} from "recharts";
import { api, setAuthToken } from "./api/client";

/* =========================================================================
   DESIGN TOKENS
   ========================================================================= */
const C = {
  bg: "#0A1628",
  bg2: "#0F1F3A",
  bg3: "#16294D",
  border: "#1F3159",
  borderHover: "#2A4373",
  gold: "#D4AF37",
  goldSoft: "#B8962E",
  text: "#F5F7FA",
  text2: "#A8B4CC",
  text3: "#6B7A9E",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
  info: "#3B82F6",
};

const GLOBAL_CSS = `
  .wcc-root, .wcc-root * { font-family: 'Inter', system-ui, -apple-system, sans-serif; }
  .wcc-root { color: ${C.text}; background: ${C.bg}; }
  .wcc-num { font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }
  .wcc-h { letter-spacing: -0.01em; font-weight: 600; }
  .wcc-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
  .wcc-scroll::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 999px; }
  .wcc-scroll::-webkit-scrollbar-thumb:hover { background: ${C.borderHover}; }
  .wcc-scroll::-webkit-scrollbar-track { background: transparent; }
  .wcc-btn { transition: all 200ms cubic-bezier(0.4,0,0.2,1); }
  .wcc-btn:focus-visible { outline: none; box-shadow: 0 0 0 2px rgba(212,175,55,0.4); }
  .wcc-row { transition: background 200ms cubic-bezier(0.4,0,0.2,1); }
  .wcc-row:hover { background: ${C.bg3}; }
  .wcc-card { transition: all 200ms cubic-bezier(0.4,0,0.2,1); }
  .wcc-input { transition: all 200ms cubic-bezier(0.4,0,0.2,1); background: ${C.bg}; border: 1px solid ${C.border}; color: ${C.text}; }
  .wcc-input:focus { outline: none; border-color: ${C.gold}; box-shadow: 0 0 0 2px rgba(212,175,55,0.2); }
  .wcc-input::placeholder { color: ${C.text3}; }
  .wcc-shimmer { background: linear-gradient(90deg, ${C.bg3} 0%, ${C.borderHover} 50%, ${C.bg3} 100%); background-size: 200% 100%; animation: wccShimmer 1.5s ease-in-out infinite; }
  @keyframes wccShimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  @keyframes wccFadeIn { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
  .wcc-fade { animation: wccFadeIn 200ms cubic-bezier(0.4,0,0.2,1); }
  @keyframes wccSlideIn { from{transform:translateX(100%)} to{transform:translateX(0)} }
  .wcc-slide { animation: wccSlideIn 220ms cubic-bezier(0.4,0,0.2,1); }
  @keyframes wccToastIn { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
  .wcc-toast { animation: wccToastIn 220ms cubic-bezier(0.4,0,0.2,1); }
  .wcc-scale-in { animation: wccScaleIn 180ms cubic-bezier(0.4,0,0.2,1); }
  @keyframes wccScaleIn { from{opacity:0;transform:scale(0.96)} to{opacity:1;transform:scale(1)} }
  .wcc-link:hover { color: ${C.gold}; }
  .wcc-hover-border:hover { border-color: ${C.borderHover}; }
`;

/* =========================================================================
   LIVE DATA — populated from the API at runtime (see /api/client.js)
   ========================================================================= */
const HOUR = 3600 * 1000;
const DAY = 24 * HOUR;
const iso = (ms) => new Date(ms).toISOString();
// NOW is fixed at the moment the app is loaded — good enough for relative-time
// labels ("2h ago") within a session; refresh the page for a new reference point.
const NOW = Date.now();

const CATEGORIES = ["Sales", "Marketing", "Operations", "Compliance", "Client Servicing", "Reporting", "IT & Technology", "Finance & Accounts", "Legal", "Content & Design", "Business Development", "Training", "HR", "Admin", "Other"];
const STATUSES = ["draft", "assigned", "in_progress", "awaiting_help", "help_in_progress", "completed", "closed"];
const PRIORITIES = ["low", "medium", "high", "urgent"];

/* =========================================================================
   UTILITIES
   ========================================================================= */
// Role hierarchy, lowest to highest — mirrors middleware/auth.js exactly.
const roleRank = {
  Intern: 1,
  "Team Member": 2,
  "Team Lead": 3,
  Manager: 4,
  "Senior Manager": 5,
  "Associate Director": 6,
  Admin: 7,
  Director: 8,
  "CEO & Founder": 9,
};
const byId = (arr) => Object.fromEntries(arr.map((x) => [x.id, x]));
// Mutable on purpose: populated in place once /api/bootstrap resolves (see WCCTaskFlow()),
// so every component below can keep reading `userMap[id]` exactly as the original prototype did.
const userMap = {};

function isAncestor(userId, targetId, users) {
  if (!targetId) return false;
  let cur = users.find((u) => u.id === targetId);
  while (cur && cur.managerId) {
    if (cur.managerId === userId) return true;
    cur = users.find((u) => u.id === cur.managerId);
  }
  return false;
}
function isAbove(viewer, target, users) {
  if (!viewer || !target) return false;
  if (viewer.id === target.id) return false;
  if (isAncestor(viewer.id, target.id, users)) return true;
  return roleRank[viewer.role] > roleRank[target.role];
}
function canManageAll(user) { return roleRank[user.role] >= roleRank["Admin"]; }
function canSeeReports(user) { return roleRank[user.role] >= roleRank["Manager"]; }
function canSeeTeam(user)    { return roleRank[user.role] >= roleRank["Team Lead"]; }

function directReports(userId, users) { return users.filter((u) => u.managerId === userId); }
function allReports(userId, users) {
  const out = [];
  const stack = [userId];
  while (stack.length) {
    const id = stack.pop();
    directReports(id, users).forEach((r) => { out.push(r); stack.push(r.id); });
  }
  return out;
}

function relTime(dateStr) {
  const t = new Date(dateStr).getTime();
  const diff = t - NOW;
  const abs = Math.abs(diff);
  const past = diff < 0;
  const m = Math.round(abs / (60 * 1000));
  if (m < 1) return past ? "just now" : "in a moment";
  if (m < 60) return past ? `${m}m ago` : `in ${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return past ? `${h}h ago` : `in ${h}h`;
  const d = Math.round(h / 24);
  if (d < 30) return past ? `${d}d ago` : `in ${d}d`;
  const mo = Math.round(d / 30);
  return past ? `${mo}mo ago` : `in ${mo}mo`;
}
function absDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true });
}
function fmtDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}
function isOverdue(task) {
  return ["assigned","in_progress","awaiting_help","help_in_progress"].includes(task.status) &&
    new Date(task.dueDate).getTime() < NOW;
}
function isDueToday(task) {
  const d = new Date(task.dueDate);
  const now = new Date(NOW);
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

function nextFires(reminder, count = 5) {
  const out = [];
  const now = new Date(NOW);
  const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const parseHM = (s) => { const [h, m] = s.split(":").map(Number); return { h, m }; };
  if (reminder.type === "once") {
    return [absDate(reminder.config.at)];
  }
  if (reminder.type === "daily") {
    const { h, m } = parseHM(reminder.config.at);
    let d = new Date(now); d.setHours(h, m, 0, 0);
    if (d < now) d.setDate(d.getDate() + 1);
    for (let i = 0; i < count; i++) { out.push(absDate(iso(d.getTime()))); d = new Date(d.getTime() + DAY); }
    return out;
  }
  if (reminder.type === "multi_daily") {
    const times = reminder.config.times.map(parseHM);
    let d = new Date(now); d.setHours(0,0,0,0);
    while (out.length < count) {
      for (const t of times) {
        const fire = new Date(d); fire.setHours(t.h, t.m, 0, 0);
        if (fire > now) { out.push(absDate(iso(fire.getTime()))); if (out.length === count) break; }
      }
      d = new Date(d.getTime() + DAY);
    }
    return out;
  }
  if (reminder.type === "weekly") {
    const { h, m } = parseHM(reminder.config.at);
    const dayIdx = days.indexOf(reminder.config.day);
    let d = new Date(now);
    while (d.getDay() !== dayIdx) d.setDate(d.getDate() + 1);
    d.setHours(h, m, 0, 0);
    if (d < now) d.setDate(d.getDate() + 7);
    for (let i = 0; i < count; i++) { out.push(absDate(iso(d.getTime()))); d = new Date(d.getTime() + 7 * DAY); }
    return out;
  }
  if (reminder.type === "hourly_office") {
    const { h: fh, m: fm } = parseHM(reminder.config.from);
    const { h: th, m: tm } = parseHM(reminder.config.to);
    let d = new Date(now); d.setHours(fh, fm, 0, 0);
    while (out.length < count) {
      const dayName = days[d.getDay()];
      if (reminder.config.days.includes(dayName)) {
        let cur = new Date(d);
        const end = new Date(d); end.setHours(th, tm, 0, 0);
        while (cur <= end && out.length < count) {
          if (cur > now) out.push(absDate(iso(cur.getTime())));
          cur = new Date(cur.getTime() + HOUR);
        }
      }
      d.setDate(d.getDate() + 1); d.setHours(fh, fm, 0, 0);
    }
    return out;
  }
  if (reminder.type === "custom") {
    let d = new Date(now);
    for (let i = 0; i < count; i++) { d = new Date(d.getTime() + 2 * HOUR); out.push(absDate(iso(d.getTime()))); }
    return out;
  }
  return [];
}

function reminderLabel(r) {
  if (r.type === "once") return `Once at ${absDate(r.config.at)}`;
  if (r.type === "daily") return `Daily at ${r.config.at}`;
  if (r.type === "multi_daily") return `${r.config.times.length}× daily · ${r.config.times.join(", ")}`;
  if (r.type === "weekly") return `Weekly · ${r.config.day} at ${r.config.at}`;
  if (r.type === "hourly_office") return `Hourly · ${r.config.from}–${r.config.to} · ${r.config.days.join(", ")}`;
  if (r.type === "custom") return `Custom · ${r.config.cron}`;
  return r.type;
}

/* =========================================================================
   PRIMITIVES
   ========================================================================= */
function WCCMonogram({ size = 32 }) {
  // The logo is navy-on-transparent, so it needs a light backing to stay
  // visible against the app's dark navy background — a small white rounded
  // chip, sized and padded proportionally to whatever size is requested.
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.22,
        background: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      <img
        src="/wcc-logo.png"
        alt="WCC Securities"
        style={{ width: "78%", height: "78%", objectFit: "contain" }}
      />
    </div>
  );
}

// The backend may return a relative avatar path (e.g. "/uploads/avatars/x.jpg").
// In production the frontend and API share one origin, so a relative path
// works as-is. In local dev they're on different ports (3000 vs 5000), so a
// relative path would resolve against the wrong one — this builds the full
// URL against wherever the API actually lives.
function resolveMediaUrl(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url; // already absolute
  const apiOrigin = (process.env.REACT_APP_API_URL || "http://localhost:5000/api").replace(/\/api\/?$/, "");
  return `${apiOrigin}${url}`;
}

function Avatar({ user, size = 32, ring = false }) {
  if (!user) return null;
  const dim = { width: size, height: size, fontSize: Math.round(size * 0.38) };
  if (user.avatarUrl) {
    return (
      <div
        className="rounded-full flex-shrink-0"
        style={{
          width: size,
          height: size,
          overflow: "hidden",
          boxShadow: ring ? `0 0 0 2px ${C.bg}, 0 0 0 3px ${C.gold}` : "none",
          display: "inline-block",
        }}
        title={user.name}
      >
        <img
          src={resolveMediaUrl(user.avatarUrl)}
          alt={user.name}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </div>
    );
  }
  return (
    <div
      className="inline-flex items-center justify-center rounded-full wcc-h flex-shrink-0"
      style={{
        ...dim,
        background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldSoft} 100%)`,
        color: C.bg,
        boxShadow: ring ? `0 0 0 2px ${C.bg}, 0 0 0 3px ${C.gold}` : "none",
      }}
      title={user.name}
    >
      {user.avatarInitials}
    </div>
  );
}

function Pill({ children, tone = "neutral", dot = false, className = "" }) {
  const tones = {
    neutral: { bg: C.bg3, fg: C.text2, dot: C.text3 },
    gold:    { bg: "rgba(212,175,55,0.12)", fg: C.gold, dot: C.gold },
    success: { bg: "rgba(16,185,129,0.12)",  fg: C.success, dot: C.success },
    warning: { bg: "rgba(245,158,11,0.12)",  fg: C.warning, dot: C.warning },
    danger:  { bg: "rgba(239,68,68,0.14)",   fg: C.danger, dot: C.danger },
    info:    { bg: "rgba(59,130,246,0.14)",  fg: C.info, dot: C.info },
    muted:   { bg: C.bg3, fg: C.text3, dot: C.text3 },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
      style={{ background: t.bg, color: t.fg }}
    >
      {dot && <span className="inline-block rounded-full" style={{ width: 6, height: 6, background: t.dot }} />}
      {children}
    </span>
  );
}

function PriorityPill({ priority }) {
  const map = {
    urgent: { tone: "danger",  label: "Urgent" },
    high:   { tone: "warning", label: "High" },
    medium: { tone: "info",    label: "Medium" },
    low:    { tone: "muted",   label: "Low" },
  };
  const p = map[priority] || map.low;
  return <Pill tone={p.tone}>{p.label}</Pill>;
}

function StatusPill({ status }) {
  const map = {
    draft:            { tone: "muted",   label: "Draft" },
    assigned:         { tone: "info",    label: "Assigned" },
    in_progress:      { tone: "info",    label: "In progress" },
    awaiting_help:    { tone: "warning", label: "Awaiting help" },
    help_in_progress: { tone: "warning", label: "Help in progress" },
    completed:        { tone: "success", label: "Completed" },
    closed:           { tone: "neutral", label: "Closed" },
  };
  const s = map[status] || map.draft;
  return <Pill tone={s.tone} dot>{s.label}</Pill>;
}

function Button({ children, variant = "primary", size = "md", icon: Icon, iconRight = false, className = "", ...props }) {
  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-sm",
  };
  const variants = {
    primary: { bg: C.gold,  fg: C.bg,      border: C.gold,     hoverBg: C.goldSoft },
    secondary: { bg: "transparent", fg: C.text, border: C.border, hoverBg: C.bg3 },
    ghost:   { bg: "transparent",   fg: C.text2, border: "transparent", hoverBg: C.bg3 },
    danger:  { bg: "transparent",   fg: C.danger, border: C.border, hoverBg: "rgba(239,68,68,0.12)" },
  };
  const v = variants[variant] || variants.primary;
  const [h, setH] = useState(false);
  return (
    <button
      className={`wcc-btn inline-flex items-center justify-center gap-2 rounded-lg font-medium ${sizes[size]} ${className}`}
      style={{
        background: h ? v.hoverBg : v.bg,
        color: v.fg,
        border: `1px solid ${v.border}`,
      }}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      {...props}
    >
      {Icon && !iconRight && <Icon size={16} strokeWidth={1.5} />}
      {children}
      {Icon && iconRight && <Icon size={16} strokeWidth={1.5} />}
    </button>
  );
}

function IconButton({ icon: Icon, label, ...props }) {
  return (
    <button
      className="wcc-btn inline-flex items-center justify-center rounded-lg"
      style={{ width: 36, height: 36, background: "transparent", color: C.text2, border: `1px solid transparent` }}
      onMouseEnter={(e) => { e.currentTarget.style.background = C.bg3; e.currentTarget.style.color = C.text; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.text2; }}
      aria-label={label}
      title={label}
      {...props}
    >
      <Icon size={18} strokeWidth={1.5} />
    </button>
  );
}

// A category dropdown that also lets someone type a brand-new category not
// in the preset list. Starts in "custom" text-entry mode automatically if
// the current value isn't one of the presets (e.g. an older task that
// already has a custom category), so it always displays correctly.
function CategorySelect({ value, onChange }) {
  const [customMode, setCustomMode] = useState(value && !CATEGORIES.includes(value));

  if (customMode) {
    return (
      <div className="flex items-center gap-2">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type a category…"
          className="wcc-input w-full rounded-md px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={() => { setCustomMode(false); onChange(CATEGORIES[0]); }}
          className="wcc-btn text-xs flex-shrink-0"
          style={{ color: C.text3 }}
        >
          Use preset
        </button>
      </div>
    );
  }

  return (
    <select
      value={value}
      onChange={(e) => {
        if (e.target.value === "__custom__") { setCustomMode(true); onChange(""); }
        else onChange(e.target.value);
      }}
      className="wcc-input w-full rounded-md px-3 py-2 text-sm"
    >
      {CATEGORIES.map((c) => <option key={c} value={c} style={{ background: C.bg2 }}>{c}</option>)}
      <option value="__custom__" style={{ background: C.bg2, color: C.gold }}>+ Add custom category…</option>
    </select>
  );
}

function TextInput({ icon: Icon, className = "", ...props }) {
  return (
    <div className={`relative ${className}`}>
      {Icon && <Icon size={16} strokeWidth={1.5} style={{ color: C.text3, position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />}
      <input
        {...props}
        style={{ ...(props.style || {}), paddingLeft: Icon ? "36px" : "12px", paddingRight: "12px" }}
        className={`wcc-input w-full rounded-md py-2 text-sm`}
      />
    </div>
  );
}

// A password field with a show/hide (eye) toggle. Drop-in replacement for a
// plain <input type="password">, forwards all other props unchanged.
function PasswordInput({ className = "", style = {}, ...props }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        {...props}
        type={visible ? "text" : "password"}
        className={`wcc-input w-full rounded-md py-2 text-sm ${className}`}
        style={{ ...style, paddingLeft: "12px", paddingRight: "40px" }}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="wcc-btn"
        style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: C.text3 }}
        aria-label={visible ? "Hide password" : "Show password"}
        tabIndex={-1}
      >
        {visible ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
      </button>
    </div>
  );
}

function Card({ children, className = "", padding = "p-4", hoverable = false }) {
  const [h, setH] = useState(false);
  return (
    <div
      className={`wcc-card rounded-xl ${padding} ${className}`}
      style={{
        background: C.bg2,
        border: `1px solid ${h && hoverable ? C.borderHover : C.border}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }}
      onMouseEnter={() => hoverable && setH(true)}
      onMouseLeave={() => hoverable && setH(false)}
    >
      {children}
    </div>
  );
}

function MetricCard({ label, value, trend, trendLabel, icon: Icon, tone = "neutral", accent = false }) {
  const toneColor = { neutral: C.text, gold: C.gold, success: C.success, warning: C.warning, danger: C.danger, info: C.info }[tone];
  return (
    <Card padding="p-4" hoverable>
      <div className="flex items-start justify-between mb-3">
        <div className="text-xs" style={{ color: C.text3 }}>{label}</div>
        {Icon && (
          <div className="rounded-lg p-1.5" style={{ background: accent ? "rgba(212,175,55,0.12)" : C.bg3 }}>
            <Icon size={16} strokeWidth={1.5} style={{ color: accent ? C.gold : C.text2 }} />
          </div>
        )}
      </div>
      <div className="wcc-num wcc-h text-3xl mb-1" style={{ color: toneColor }}>{value}</div>
      {trend != null && (
        <div className="flex items-center gap-1 text-xs">
          {trend >= 0
            ? <ArrowUp size={12} strokeWidth={1.5} style={{ color: C.success }} />
            : <ArrowDown size={12} strokeWidth={1.5} style={{ color: C.danger }} />}
          <span style={{ color: trend >= 0 ? C.success : C.danger }}>{Math.abs(trend)}%</span>
          <span style={{ color: C.text3 }}>{trendLabel}</span>
        </div>
      )}
    </Card>
  );
}

function EmptyState({ icon: Icon = Sparkles, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <div className="rounded-full p-4 mb-4" style={{ background: C.bg3 }}>
        <Icon size={24} strokeWidth={1.5} style={{ color: C.gold }} />
      </div>
      <div className="wcc-h text-base mb-1" style={{ color: C.text }}>{title}</div>
      {subtitle && <div className="text-sm mb-4 max-w-sm" style={{ color: C.text3 }}>{subtitle}</div>}
      {action}
    </div>
  );
}

function Modal({ open, onClose, title, children, width = 480, footer }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(4,10,20,0.72)" }} onClick={onClose}>
      <div
        className="wcc-scale-in rounded-xl overflow-hidden flex flex-col"
        style={{
          background: C.bg2,
          border: `1px solid ${C.border}`,
          boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
          width: "100%",
          maxWidth: width,
          maxHeight: "90vh",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="wcc-h text-base">{title}</div>
          <IconButton icon={X} label="Close" onClick={onClose} />
        </div>
        <div className="flex-1 overflow-y-auto wcc-scroll px-6 py-4">{children}</div>
        {footer && <div className="px-6 py-4 flex items-center justify-end gap-2" style={{ borderTop: `1px solid ${C.border}` }}>{footer}</div>}
      </div>
    </div>
  );
}

// A minimal contact-lookup card — just picture, name, email, phone. Used when
// someone clicks a person from search; intentionally does not show role,
// department, manager, or any editable fields.
function ProfileCardModal({ userId, onClose }) {
  const user = userId ? userMap[userId] : null;
  if (!user) return null;
  return (
    <Modal open={!!userId} onClose={onClose} title="Contact" width={360}>
      <div className="flex flex-col items-center text-center gap-3 py-2">
        <Avatar user={user} size={72} />
        <div className="wcc-h text-lg">{user.name}</div>
        <div className="w-full space-y-2 mt-2">
          <div className="flex items-center gap-2 rounded-md px-3 py-2 text-sm" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
            <Mail size={14} strokeWidth={1.5} style={{ color: C.text3 }} />
            <span style={{ color: C.text2 }}>{user.email || "—"}</span>
          </div>
          <div className="flex items-center gap-2 rounded-md px-3 py-2 text-sm" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
            <Phone size={14} strokeWidth={1.5} style={{ color: C.text3 }} />
            <span style={{ color: C.text2 }}>{user.phone || "—"}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Drawer({ open, onClose, children, width = 560 }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end" style={{ background: "rgba(4,10,20,0.5)" }} onClick={onClose}>
      <div
        className="wcc-slide h-full overflow-y-auto wcc-scroll"
        style={{
          background: C.bg2,
          borderLeft: `1px solid ${C.border}`,
          boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
          width: "100%",
          maxWidth: width,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/* =========================================================================
   DROPDOWN (used for role switcher, filters, etc.)
   ========================================================================= */
function Dropdown({ trigger, children, align = "right", width = 240 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <div onClick={() => setOpen(!open)}>{trigger}</div>
      {open && (
        <div
          className="wcc-fade absolute mt-2 rounded-xl overflow-hidden z-30"
          style={{
            [align === "right" ? "right" : "left"]: 0,
            background: C.bg2,
            border: `1px solid ${C.border}`,
            boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
            width,
          }}
        >
          <div className="max-h-96 overflow-y-auto wcc-scroll">
            {typeof children === "function" ? children(() => setOpen(false)) : children}
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   NAV
   ========================================================================= */
const NAV_ITEMS = [
  { key: "dashboard",     label: "Dashboard",         icon: LayoutDashboard, minRank: 1 },
  { key: "my_tasks",      label: "My Tasks",          icon: ListChecks,      minRank: 1 },
  { key: "assigned_by_me",label: "Assigned by Me",    icon: Send,            minRank: 1 },
  { key: "projects",      label: "Projects",          icon: Folder,    minRank: 1 },
  { key: "help_requests", label: "Help Requests",     icon: HandHelping,     minRank: 1 },
  { key: "team",          label: "Team",              icon: Users,           minRank: 4 },  // Manager and above
  { key: "reports",       label: "Reports",           icon: BarChart3,       minRank: 4 },  // Manager and above
  { key: "users",         label: "Users & Hierarchy", icon: UserCog,         minRank: 7 },  // Admin and above
  { key: "settings",      label: "Settings",          icon: SettingsIcon,    minRank: 1 },
];

function Sidebar({ currentScreen, setScreen, user, onSignOut, mobileOpen, setMobileOpen }) {
  const items = NAV_ITEMS.filter((n) => roleRank[user.role] >= n.minRank);
  const content = (
    <>
      <div className="p-4 flex items-center gap-2" style={{ borderBottom: `1px solid ${C.border}` }}>
        <WCCMonogram size={30} />
        <div>
          <div className="wcc-h text-sm" style={{ letterSpacing: "-0.01em" }}>TaskFlow</div>
          <div className="text-xs" style={{ color: C.text3 }}>WCC Securities</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto wcc-scroll p-2">
        {items.map((n) => {
          const active = currentScreen === n.key;
          return (
            <button
              key={n.key}
              onClick={() => { setScreen(n.key); setMobileOpen(false); }}
              className="wcc-btn w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm mb-0.5"
              style={{
                background: active ? "rgba(212,175,55,0.12)" : "transparent",
                color: active ? C.gold : C.text2,
                border: active ? `1px solid rgba(212,175,55,0.35)` : `1px solid transparent`,
                fontWeight: active ? 600 : 500,
              }}
              onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = C.bg3; e.currentTarget.style.color = C.text; } }}
              onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.text2; } }}
            >
              <n.icon size={18} strokeWidth={1.5} />
              <span>{n.label}</span>
            </button>
          );
        })}
      </nav>
      <div className="p-3" style={{ borderTop: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-3 rounded-lg p-2" style={{ background: C.bg3 }}>
          <Avatar user={user} size={36} />
          <div className="flex-1 min-w-0">
            <div className="text-sm wcc-h truncate">{user.name}</div>
            <div className="text-xs truncate" style={{ color: C.text3 }}>{user.role}</div>
          </div>
          <IconButton icon={LogOut} label="Sign out" onClick={onSignOut} />
        </div>
      </div>
    </>
  );
  return (
    <>
      <aside className="hidden md:flex flex-col" style={{ width: 240, background: C.bg2, borderRight: `1px solid ${C.border}`, height: "100vh" }}>
        {content}
      </aside>
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex" style={{ background: "rgba(4,10,20,0.6)" }} onClick={() => setMobileOpen(false)}>
          <aside className="wcc-slide flex flex-col" style={{ width: 260, background: C.bg2, borderRight: `1px solid ${C.border}`, height: "100vh" }} onClick={(e) => e.stopPropagation()}>
            {content}
          </aside>
        </div>
      )}
    </>
  );
}

/* =========================================================================
   TOP BAR
   ========================================================================= */
function TopBar({ user, users, setCurrentUserId, notifications, onOpenSearch, onOpenNotifications, onOpenMobileNav, unreadCount, screenTitle, onSignOut }) {
  return (
    <header className="sticky top-0 z-30 flex items-center gap-3 px-4" style={{ height: 56, background: C.bg2, borderBottom: `1px solid ${C.border}` }}>
      <button className="md:hidden wcc-btn p-1.5 rounded-lg" style={{ color: C.text2 }} onClick={onOpenMobileNav}><Menu size={20} strokeWidth={1.5} /></button>
      <div className="md:hidden flex items-center gap-2">
        <WCCMonogram size={28} />
      </div>
      <div className="hidden md:block wcc-h text-sm" style={{ color: C.text2 }}>{screenTitle}</div>
      <div className="flex-1 flex justify-center">
        <button
          onClick={onOpenSearch}
          className="wcc-btn hidden sm:flex items-center gap-2 rounded-lg px-3 py-1.5"
          style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text3, width: 384, maxWidth: "100%" }}
        >
          <Search size={16} strokeWidth={1.5} />
          <span className="text-sm flex-1 text-left">Search tasks, people, tags…</span>
          <kbd className="text-xs px-1.5 py-0.5 rounded" style={{ background: C.bg3, color: C.text2, border: `1px solid ${C.border}` }}>⌘K</kbd>
        </button>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={onOpenNotifications}
          className="wcc-btn relative rounded-lg"
          style={{ width: 36, height: 36, color: C.text2, background: "transparent" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = C.bg3; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          aria-label="Notifications"
        >
          <Bell size={18} strokeWidth={1.5} className="mx-auto" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 wcc-num rounded-full text-xs flex items-center justify-center"
              style={{ minWidth: 18, height: 18, padding: "0 4px", background: C.gold, color: C.bg, fontWeight: 700 }}
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
        <Dropdown
          align="right"
          width={280}
          trigger={
            <button
              className="wcc-btn flex items-center gap-2 rounded-lg pl-1.5 pr-2 py-1"
              style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
            >
              <Avatar user={user} size={26} />
              <div className="hidden sm:flex flex-col items-start leading-tight">
                <span className="text-xs" style={{ color: C.text3 }}>Signed in as</span>
                <span className="text-xs wcc-h">{user.name.split(" ")[0]} · {user.role}</span>
              </div>
              <ChevronDown size={14} strokeWidth={1.5} style={{ color: C.text3 }} />
            </button>
          }
        >
          {(close) => (
            <div>
              <div className="px-4 py-3 flex items-center gap-3" style={{ borderBottom: `1px solid ${C.border}` }}>
                <Avatar user={user} size={38} />
                <div className="min-w-0">
                  <div className="text-sm wcc-h truncate">{user.name}</div>
                  <div className="text-xs truncate" style={{ color: C.text3 }}>{user.role} · {user.department}</div>
                  <div className="text-xs truncate" style={{ color: C.text3 }}>{user.email}</div>
                </div>
              </div>
              <div className="p-1">
                <button
                  onClick={() => { close(); if (onSignOut) onSignOut(); }}
                  className="wcc-btn w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm"
                  onMouseEnter={(e) => e.currentTarget.style.background = C.bg3}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  style={{ color: "#F87171" }}
                >
                  <LogOut size={15} strokeWidth={1.5} /> Sign out
                </button>
              </div>
            </div>
          )}
        </Dropdown>
      </div>
    </header>
  );
}

/* =========================================================================
   NOTIFICATIONS DROPDOWN
   ========================================================================= */
function NotificationsPanel({ open, onClose, notifications, users, markAllRead, onOpenTask }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div
        className="wcc-fade absolute rounded-xl overflow-hidden"
        style={{
          top: 60,
          right: 16,
          width: 380,
          maxHeight: "70vh",
          background: C.bg2,
          border: `1px solid ${C.border}`,
          boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="wcc-h text-sm">Notifications</div>
          <button className="text-xs wcc-link" style={{ color: C.gold }} onClick={markAllRead}>Mark all as read</button>
        </div>
        <div className="overflow-y-auto wcc-scroll" style={{ maxHeight: "60vh" }}>
          {notifications.length === 0 ? (
            <EmptyState icon={Bell} title="You're all caught up" subtitle="New task activity will show up here." />
          ) : (
            notifications.map((n) => {
              const from = users.find((u) => u.id === n.userId);
              const clickable = !!n.taskId;
              return (
                <div key={n.id}
                  onClick={() => { if (clickable && onOpenTask) onOpenTask(n.taskId, n.id); }}
                  className="px-4 py-3 flex gap-3"
                  style={{ borderBottom: `1px solid ${C.border}`, background: n.read ? "transparent" : "rgba(212,175,55,0.04)", cursor: clickable ? "pointer" : "default" }}
                  onMouseEnter={(e) => { if (clickable) e.currentTarget.style.background = "rgba(212,175,55,0.10)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = n.read ? "transparent" : "rgba(212,175,55,0.04)"; }}>
                  {!n.read && <div className="mt-1.5 rounded-full flex-shrink-0" style={{ width: 6, height: 6, background: C.gold }} />}
                  {n.read && <div className="mt-1.5 flex-shrink-0" style={{ width: 6, height: 6 }} />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm" style={{ color: C.text }}>{n.message}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs" style={{ color: C.text3 }}>{relTime(n.createdAt)}</span>
                      <span style={{ color: C.text3 }}>·</span>
                      <div className="flex items-center gap-1">
                        {n.channels.includes("inapp") && <Bell size={11} strokeWidth={1.5} style={{ color: C.text3 }} />}
                        {n.channels.includes("email") && <Mail size={11} strokeWidth={1.5} style={{ color: C.text3 }} />}
                        {n.channels.includes("whatsapp") && <MessageCircle size={11} strokeWidth={1.5} style={{ color: C.text3 }} />}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   LOGIN
   ========================================================================= */
function LoginScreen({ onSignIn, loading, error, onForgotPassword }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (email && password && !loading) onSignIn(email.trim(), password);
  };
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: C.bg }}>
      <div
        className="wcc-scale-in rounded-xl overflow-hidden"
        style={{ background: C.bg2, border: `1px solid ${C.border}`, width: "100%", maxWidth: 420, boxShadow: "0 4px 24px rgba(0,0,0,0.35)" }}
      >
        <form onSubmit={submit} className="p-8 flex flex-col items-center">
          <WCCMonogram size={56} />
          <div className="wcc-h text-2xl mt-4">TaskFlow</div>
          <div className="text-xs mt-1" style={{ color: C.text3 }}>WCC Securities · Internal</div>
          <div className="text-sm mt-6 mb-4" style={{ color: C.text2 }}>Sign in to continue</div>
          <div className="w-full mb-3">
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Work email</label>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@wccsecurities.com"
              className="wcc-input w-full rounded-md px-3 py-2 text-sm"
              required
            />
          </div>
          <div className="w-full mb-1.5">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs block" style={{ color: C.text3 }}>Password</label>
              <button type="button" onClick={onForgotPassword} className="text-xs wcc-link" style={{ color: C.gold }}>
                Forgot password?
              </button>
            </div>
            <PasswordInput
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          {error ? (
            <div className="w-full text-xs mb-2 px-3 py-2 rounded-md" style={{ background: "rgba(239,68,68,0.12)", color: C.danger, border: `1px solid rgba(239,68,68,0.3)` }}>
              {error}
            </div>
          ) : null}
          <Button type="submit" className="w-full mt-2" onClick={submit}>{loading ? "Signing in…" : "Sign in"}</Button>
          <div className="text-xs mt-6 text-center" style={{ color: C.text3 }}>
            Contact your admin if you don't have login credentials yet.
          </div>
        </form>
      </div>
    </div>
  );
}

/* =========================================================================
   FORGOT PASSWORD
   ========================================================================= */
function ForgotPasswordScreen({ onRequestReset, onBackToLogin }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || loading) return;
    setLoading(true);
    setError("");
    try {
      await onRequestReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.error || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: C.bg }}>
      <div
        className="wcc-scale-in rounded-xl overflow-hidden"
        style={{ background: C.bg2, border: `1px solid ${C.border}`, width: "100%", maxWidth: 420, boxShadow: "0 4px 24px rgba(0,0,0,0.35)" }}
      >
        <div className="p-8 flex flex-col items-center">
          <WCCMonogram size={56} />
          <div className="wcc-h text-2xl mt-4">Reset password</div>
          {sent ? (
            <>
              <div className="text-sm mt-4 text-center" style={{ color: C.text2 }}>
                If an account exists for <span className="wcc-h" style={{ color: C.text }}>{email}</span>, we've sent a link to reset the password. It expires in 1 hour.
              </div>
              <Button variant="secondary" className="w-full mt-6" onClick={onBackToLogin}>Back to sign in</Button>
            </>
          ) : (
            <form onSubmit={submit} className="w-full">
              <div className="text-sm mt-2 mb-6 text-center" style={{ color: C.text2 }}>
                Enter your work email and we'll send you a link to reset your password.
              </div>
              <div className="w-full mb-3">
                <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Work email</label>
                <input
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@wccsecurities.com"
                  className="wcc-input w-full rounded-md px-3 py-2 text-sm"
                  autoFocus
                  required
                />
              </div>
              {error ? (
                <div className="w-full text-xs mb-2 px-3 py-2 rounded-md" style={{ background: "rgba(239,68,68,0.12)", color: C.danger, border: `1px solid rgba(239,68,68,0.3)` }}>
                  {error}
                </div>
              ) : null}
              <Button type="submit" className="w-full mt-2" onClick={submit}>{loading ? "Sending…" : "Send reset link"}</Button>
              <button type="button" onClick={onBackToLogin} className="text-xs mt-4 w-full text-center wcc-link" style={{ color: C.text3 }}>
                Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   RESET PASSWORD (reached via emailed link: /?reset_token=...)
   ========================================================================= */
function ResetPasswordScreen({ token, onResetPassword, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    setError("");
    try {
      await onResetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.error || "This reset link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: C.bg }}>
      <div
        className="wcc-scale-in rounded-xl overflow-hidden"
        style={{ background: C.bg2, border: `1px solid ${C.border}`, width: "100%", maxWidth: 420, boxShadow: "0 4px 24px rgba(0,0,0,0.35)" }}
      >
        <div className="p-8 flex flex-col items-center">
          <WCCMonogram size={56} />
          <div className="wcc-h text-2xl mt-4">Choose a new password</div>
          {done ? (
            <>
              <div className="text-sm mt-4 text-center" style={{ color: C.text2 }}>
                Your password has been updated. You can now sign in with it.
              </div>
              <Button className="w-full mt-6" onClick={onDone}>Go to sign in</Button>
            </>
          ) : (
            <form onSubmit={submit} className="w-full">
              <div className="w-full mt-4 mb-3">
                <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>New password</label>
                <PasswordInput
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoFocus
                  required
                />
              </div>
              <div className="w-full mb-3">
                <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Confirm new password</label>
                <PasswordInput
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Retype the password"
                  required
                />
              </div>
              {error ? (
                <div className="w-full text-xs mb-2 px-3 py-2 rounded-md" style={{ background: "rgba(239,68,68,0.12)", color: C.danger, border: `1px solid rgba(239,68,68,0.3)` }}>
                  {error}
                </div>
              ) : null}
              <Button type="submit" className="w-full mt-2" onClick={submit}>{loading ? "Saving…" : "Save new password"}</Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   DASHBOARD — EMPLOYEE
   ========================================================================= */
function DashboardEmployee({ user, tasks, activity, onOpenTask, helpRequests }) {
  const myTasks = tasks.filter((t) => t.assignedTo === user.id);
  const open = myTasks.filter((t) => !["completed","closed","draft"].includes(t.status));
  const dueToday = myTasks.filter((t) => isDueToday(t) && !["completed","closed"].includes(t.status));
  const overdue = myTasks.filter(isOverdue);
  const awaitingHelp = helpRequests.filter((h) => h.helperUserId === user.id && h.status === "pending");
  const upcoming = open
    .filter((t) => {
      const d = new Date(t.dueDate).getTime();
      return d >= NOW - DAY && d <= NOW + 7 * DAY;
    })
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
    .slice(0, 8);

  const myTaskIds = new Set(myTasks.map((t) => t.id));
  const feed = activity
    .filter((a) => myTaskIds.has(a.taskId))
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, 8);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label="Open tasks" value={open.length} icon={ListChecks} tone="gold" accent trend={-8} trendLabel="vs last week" />
        <MetricCard label="Due today" value={dueToday.length} icon={Clock} tone="warning" />
        <MetricCard label="Overdue" value={overdue.length} icon={AlertTriangle} tone="danger" />
        <MetricCard label="Awaiting my help" value={awaitingHelp.length} icon={HandHelping} tone="info" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2" padding="p-0">
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.border}` }}>
            <div className="wcc-h text-sm">My tasks — next 7 days</div>
            <Pill tone="neutral">{upcoming.length}</Pill>
          </div>
          {upcoming.length === 0 ? (
            <EmptyState icon={CheckCircle2} title="Nothing due this week" subtitle="You're clear for the next 7 days." />
          ) : (
            <div className="divide-y" style={{ borderColor: C.border }}>
              {upcoming.map((t) => (
                <div key={t.id} className="wcc-row cursor-pointer px-4 py-3 flex items-center gap-3" onClick={() => onOpenTask(t.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{t.title}</div>
                    <div className="text-xs mt-0.5 flex items-center gap-2" style={{ color: C.text3 }}>
                      <span>{t.category}</span>
                      <span>·</span>
                      <span title={absDate(t.dueDate)}>{isOverdue(t) ? <span style={{ color: C.danger }}>Overdue · {relTime(t.dueDate)}</span> : `Due ${relTime(t.dueDate)}`}</span>
                    </div>
                  </div>
                  <PriorityPill priority={t.priority} />
                  <StatusPill status={t.status} />
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card padding="p-0">
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
            <div className="wcc-h text-sm">Recent activity</div>
          </div>
          <div className="p-2">
            {feed.length === 0 ? (
              <EmptyState icon={History} title="No recent activity" />
            ) : (
              feed.map((a) => {
                const u = userMap[a.userId] || { name: "Unknown user", avatarInitials: "?" };
                const t = tasks.find((x) => x.id === a.taskId);
                return (
                  <div key={a.id} className="p-2 flex gap-3 items-start">
                    <Avatar user={u} size={28} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs" style={{ color: C.text }}>
                        <span className="wcc-h">{u.name}</span>{" "}
                        <span style={{ color: C.text3 }}>{a.details}</span>
                      </div>
                      <div className="text-xs mt-0.5 truncate" style={{ color: C.text3 }}>
                        {t?.title} · {relTime(a.timestamp)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* =========================================================================
   DASHBOARD — TEAM LEAD / MANAGER
   ========================================================================= */
function DashboardManager({ user, tasks, users, onOpenTask, helpRequests }) {
  const team = [user, ...allReports(user.id, users)];
  const teamIds = new Set(team.map((u) => u.id));
  const teamTasks = tasks.filter((t) => teamIds.has(t.assignedTo));
  const open = teamTasks.filter((t) => !["completed","closed","draft"].includes(t.status));
  const overdue = teamTasks.filter(isOverdue);
  const awaitingHelp = helpRequests.filter((h) => teamIds.has(h.helperUserId) && (h.status === "pending" || h.status === "accepted"));
  const weekAgo = NOW - 7 * DAY;
  const completedWeek = teamTasks.filter((t) => t.status === "completed" && new Date(t.createdAt).getTime() > weekAgo - 30*DAY).length;

  const workload = team.slice(0, 8).map((m) => {
    const mt = teamTasks.filter((t) => t.assignedTo === m.id);
    return {
      name: m.avatarInitials,
      fullName: m.name,
      assigned: mt.filter((t) => t.status === "assigned").length,
      in_progress: mt.filter((t) => t.status === "in_progress").length,
      awaiting: mt.filter((t) => ["awaiting_help","help_in_progress"].includes(t.status)).length,
    };
  });

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label="Team open tasks" value={open.length} icon={ListChecks} tone="gold" accent />
        <MetricCard label="Team overdue" value={overdue.length} icon={AlertTriangle} tone="danger" />
        <MetricCard label="Awaiting help" value={awaitingHelp.length} icon={HandHelping} tone="warning" />
        <MetricCard label="Completed this week" value={completedWeek} icon={CheckCircle2} tone="success" trend={12} trendLabel="vs last week" />
      </div>

      <Card padding="p-0">
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="wcc-h text-sm">Team workload</div>
          <div className="flex items-center gap-3 text-xs" style={{ color: C.text3 }}>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: C.info }} /> Assigned</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: C.gold }} /> In progress</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: C.warning }} /> Awaiting help</span>
          </div>
        </div>
        <div className="p-4" style={{ height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={workload} layout="vertical" barSize={16}>
              <CartesianGrid stroke={C.border} horizontal={false} />
              <XAxis type="number" stroke={C.text3} tick={{ fill: C.text3, fontSize: 11 }} />
              <YAxis type="category" dataKey="name" stroke={C.text3} tick={{ fill: C.text2, fontSize: 11 }} width={40} />
              <Tooltip contentStyle={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="assigned" stackId="a" fill={C.info} radius={[0,0,0,0]} />
              <Bar dataKey="in_progress" stackId="a" fill={C.gold} />
              <Bar dataKey="awaiting" stackId="a" fill={C.warning} radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card padding="p-0">
        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="wcc-h text-sm">Overdue alerts</div>
        </div>
        {overdue.length === 0 ? (
          <EmptyState icon={CheckCircle2} title="No overdue tasks" subtitle="Team is on schedule." />
        ) : (
          <div className="divide-y" style={{ borderColor: C.border }}>
            {overdue.slice(0, 8).map((t) => {
              const a = userMap[t.assignedTo];
              return (
                <div
                  key={t.id}
                  className="wcc-row cursor-pointer pl-4 pr-4 py-3 flex items-center gap-3"
                  style={{ borderLeft: `3px solid ${C.danger}` }}
                  onClick={() => onOpenTask(t.id)}
                >
                  <Avatar user={a} size={30} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{t.title}</div>
                    <div className="text-xs mt-0.5" style={{ color: C.text3 }}>
                      Assigned to {a.name} · Due {absDate(t.dueDate)}
                    </div>
                  </div>
                  <Pill tone="danger" dot>Overdue {relTime(t.dueDate)}</Pill>
                  <PriorityPill priority={t.priority} />
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

/* =========================================================================
   DASHBOARD — DIRECTOR / ADMIN
   ========================================================================= */
function DashboardDirector({ tasks, onOpenTask }) {
  const total = tasks.length;
  const open = tasks.filter((t) => !["completed","closed","draft"].includes(t.status));
  const overdue = tasks.filter(isOverdue);
  const inHelp = tasks.filter((t) => ["awaiting_help","help_in_progress"].includes(t.status));
  const completed30 = tasks.filter((t) => t.status === "completed" || t.status === "closed").length;
  const completionRate = Math.round((completed30 / Math.max(1, total)) * 100);

  const byDept = CATEGORIES.map((c, i) => {
    const count = tasks.filter((t) => t.category === c).length;
    const palette = [C.gold, C.info, C.success, C.warning, C.danger, "#8B5CF6", "#EC4899", C.text3];
    return { name: c, value: count, fill: palette[i % palette.length] };
  }).filter((d) => d.value > 0);

  const trend = Array.from({ length: 14 }).map((_, i) => {
    const day = new Date(NOW - (13 - i) * DAY);
    return {
      day: day.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
      completed: 3 + Math.round(Math.sin(i / 2) * 2 + i * 0.4),
      created: 4 + Math.round(Math.cos(i / 2.5) * 2 + i * 0.3),
    };
  });

  const bottlenecks = tasks.filter((t) => {
    if (t.status === "awaiting_help") {
      return NOW - new Date(t.createdAt).getTime() > 2 * DAY;
    }
    return isOverdue(t) && NOW - new Date(t.dueDate).getTime() > 7 * DAY;
  });

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <MetricCard label="Total open" value={open.length} icon={ListChecks} tone="gold" accent />
        <MetricCard label="Overdue" value={overdue.length} icon={AlertTriangle} tone="danger" trend={Math.round((overdue.length / Math.max(1, total)) * 100)} trendLabel="of total" />
        <MetricCard label="In help loop" value={inHelp.length} icon={HandHelping} tone="warning" />
        <MetricCard label="Completion rate" value={`${completionRate}%`} icon={CheckCircle2} tone="success" trend={6} trendLabel="vs prev 30d" />
        <MetricCard label="Avg. time to complete" value="3.4d" icon={Clock} tone="info" trend={-11} trendLabel="vs prev 30d" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card padding="p-0">
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
            <div className="wcc-h text-sm">Tasks by department</div>
          </div>
          <div className="p-4 flex items-center gap-4" style={{ height: 280 }}>
            <div style={{ width: 200, height: 200 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={byDept} dataKey="value" innerRadius={50} outerRadius={80} strokeWidth={0}>
                    {byDept.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-1.5">
              {byDept.map((d) => (
                <div key={d.name} className="flex items-center gap-2 text-xs">
                  <span className="rounded-sm" style={{ width: 10, height: 10, background: d.fill }} />
                  <span className="flex-1" style={{ color: C.text2 }}>{d.name}</span>
                  <span className="wcc-num" style={{ color: C.text }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card padding="p-0">
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
            <div className="wcc-h text-sm">Completion trend · last 14 days</div>
          </div>
          <div className="p-4" style={{ height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={trend}>
                <CartesianGrid stroke={C.border} vertical={false} />
                <XAxis dataKey="day" stroke={C.text3} tick={{ fill: C.text3, fontSize: 10 }} />
                <YAxis stroke={C.text3} tick={{ fill: C.text3, fontSize: 10 }} />
                <Tooltip contentStyle={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }} />
                <Legend wrapperStyle={{ color: C.text2, fontSize: 11 }} />
                <Line type="monotone" dataKey="completed" name="Completed" stroke={C.gold} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="created" name="Created" stroke={C.info} strokeWidth={2} dot={false} strokeDasharray="4 4" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card padding="p-0">
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div className="wcc-h text-sm">Bottlenecks</div>
            <div className="text-xs mt-0.5" style={{ color: C.text3 }}>Stuck in awaiting-help &gt; 48h, or overdue &gt; 7 days</div>
          </div>
          <Pill tone="danger">{bottlenecks.length}</Pill>
        </div>
        {bottlenecks.length === 0 ? (
          <EmptyState icon={CheckCircle2} title="No bottlenecks" subtitle="Nothing has been stuck long enough to flag." />
        ) : (
          <div className="divide-y" style={{ borderColor: C.border }}>
            {bottlenecks.slice(0, 10).map((t) => (
              <div key={t.id} className="wcc-row cursor-pointer px-4 py-3 flex items-center gap-3" onClick={() => onOpenTask(t.id)}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{t.title}</div>
                  <div className="text-xs mt-0.5" style={{ color: C.text3 }}>
                    {(userMap[t.assignedTo]?.name || "Unknown user")} · {t.category} · Due {absDate(t.dueDate)}
                  </div>
                </div>
                <StatusPill status={t.status} />
                <PriorityPill priority={t.priority} />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* =========================================================================
   MY TASKS + ASSIGNED BY ME (shared table)
   ========================================================================= */
function TaskTableScreen({ mode, user, tasks, onOpenTask, onNewTask, users }) {
  const [statusFilter, setStatusFilter] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [tagQuery, setTagQuery] = useState("");
  const [dateRange, setDateRange] = useState("any");

  const source = mode === "my_tasks"
    ? tasks.filter((t) => t.assignedTo === user.id)
    : tasks.filter((t) => t.createdBy === user.id);

  const filtered = source.filter((t) => {
    if (statusFilter.length && !statusFilter.includes(t.status)) return false;
    if (priorityFilter.length && !priorityFilter.includes(t.priority)) return false;
    if (categoryFilter && t.category !== categoryFilter) return false;
    if (tagQuery && !t.tags.some((tg) => tg.toLowerCase().includes(tagQuery.toLowerCase()))) return false;
    const due = new Date(t.dueDate).getTime();
    if (dateRange === "today" && !isDueToday(t)) return false;
    if (dateRange === "week"  && !(due >= NOW && due <= NOW + 7 * DAY)) return false;
    if (dateRange === "overdue" && !isOverdue(t)) return false;
    return true;
  });

  const toggle = (arr, val, setter) => {
    setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const title = mode === "my_tasks" ? "My Tasks" : "Assigned by Me";

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="wcc-h text-xl">{title}</div>
          <div className="text-sm mt-0.5" style={{ color: C.text3 }}>
            {filtered.length} of {source.length} {source.length === 1 ? "task" : "tasks"}
          </div>
        </div>
        <Button icon={Plus} onClick={onNewTask}>New task</Button>
      </div>

      <Card padding="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 text-xs mr-1" style={{ color: C.text3 }}>
            <Filter size={14} strokeWidth={1.5} /> Filters
          </div>

          <Dropdown
            width={220}
            trigger={
              <button className="wcc-btn rounded-md px-3 py-1.5 text-xs flex items-center gap-1" style={{ background: C.bg3, color: C.text, border: `1px solid ${C.border}` }}>
                Status {statusFilter.length > 0 && <span className="wcc-num" style={{ color: C.gold }}>· {statusFilter.length}</span>}
                <ChevronDown size={12} strokeWidth={1.5} />
              </button>
            }
          >
            <div className="p-2">
              {STATUSES.map((s) => (
                <label key={s} className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm" style={{ color: C.text }}
                  onMouseEnter={(e) => e.currentTarget.style.background = C.bg3}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                  <input type="checkbox" checked={statusFilter.includes(s)} onChange={() => toggle(statusFilter, s, setStatusFilter)} />
                  <StatusPill status={s} />
                </label>
              ))}
            </div>
          </Dropdown>

          <Dropdown
            width={200}
            trigger={
              <button className="wcc-btn rounded-md px-3 py-1.5 text-xs flex items-center gap-1" style={{ background: C.bg3, color: C.text, border: `1px solid ${C.border}` }}>
                Priority {priorityFilter.length > 0 && <span className="wcc-num" style={{ color: C.gold }}>· {priorityFilter.length}</span>}
                <ChevronDown size={12} strokeWidth={1.5} />
              </button>
            }
          >
            <div className="p-2">
              {PRIORITIES.map((p) => (
                <label key={p} className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm"
                  onMouseEnter={(e) => e.currentTarget.style.background = C.bg3}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                  <input type="checkbox" checked={priorityFilter.includes(p)} onChange={() => toggle(priorityFilter, p, setPriorityFilter)} />
                  <PriorityPill priority={p} />
                </label>
              ))}
            </div>
          </Dropdown>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="wcc-input rounded-md px-2 py-1.5 text-xs"
            style={{ minWidth: 130 }}
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c} style={{ background: C.bg2 }}>{c}</option>)}
          </select>

          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="wcc-input rounded-md px-2 py-1.5 text-xs"
          >
            <option value="any"     style={{ background: C.bg2 }}>Any due date</option>
            <option value="today"   style={{ background: C.bg2 }}>Due today</option>
            <option value="week"    style={{ background: C.bg2 }}>Next 7 days</option>
            <option value="overdue" style={{ background: C.bg2 }}>Overdue</option>
          </select>

          <TextInput
            icon={Tag}
            placeholder="Tag search…"
            value={tagQuery}
            onChange={(e) => setTagQuery(e.target.value)}
            className="w-40"
          />

          {(statusFilter.length + priorityFilter.length + (categoryFilter ? 1 : 0) + (tagQuery ? 1 : 0) + (dateRange !== "any" ? 1 : 0)) > 0 && (
            <button
              className="text-xs wcc-link ml-auto"
              style={{ color: C.gold }}
              onClick={() => { setStatusFilter([]); setPriorityFilter([]); setCategoryFilter(""); setTagQuery(""); setDateRange("any"); }}
            >
              Clear all
            </button>
          )}
        </div>
      </Card>

      <Card padding="p-0">
        {filtered.length === 0 ? (
          <EmptyState
            icon={ListChecks}
            title="No tasks match these filters"
            subtitle="Try broadening the filters or clearing them."
            action={<Button variant="secondary" onClick={() => { setStatusFilter([]); setPriorityFilter([]); setCategoryFilter(""); setTagQuery(""); setDateRange("any"); }}>Clear filters</Button>}
          />
        ) : (
          <div className="overflow-x-auto wcc-scroll">
            <table className="w-full" style={{ minWidth: 700 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: C.text3 }}>Title</th>
                  <th className="text-left px-3 py-3 text-xs font-medium" style={{ color: C.text3 }}>Priority</th>
                  <th className="text-left px-3 py-3 text-xs font-medium" style={{ color: C.text3 }}>Due</th>
                  <th className="text-left px-3 py-3 text-xs font-medium" style={{ color: C.text3 }}>Status</th>
                  <th className="text-left px-3 py-3 text-xs font-medium" style={{ color: C.text3 }}>{mode === "my_tasks" ? "From" : "Assignee"}</th>
                  <th className="text-left px-3 py-3 text-xs font-medium" style={{ color: C.text3 }}>Reminders</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => {
                  const other = users.find((u) => u.id === (mode === "my_tasks" ? t.createdBy : t.assignedTo));
                  return (
                    <tr key={t.id} className="wcc-row cursor-pointer" style={{ borderBottom: `1px solid ${C.border}` }} onClick={() => onOpenTask(t.id)}>
                      <td className="px-4 py-3">
                        <div className="text-sm">{t.title}</div>
                        <div className="text-xs mt-0.5" style={{ color: C.text3 }}>
                          {t.category}{t.tags.length ? ` · ${t.tags.join(", ")}` : ""}
                        </div>
                      </td>
                      <td className="px-3 py-3"><PriorityPill priority={t.priority} /></td>
                      <td className="px-3 py-3">
                        <span className="text-xs" title={absDate(t.dueDate)} style={{ color: isOverdue(t) ? C.danger : C.text2 }}>
                          {isOverdue(t) ? "Overdue · " : ""}{relTime(t.dueDate)}
                        </span>
                      </td>
                      <td className="px-3 py-3"><StatusPill status={t.status} /></td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <Avatar user={other} size={22} />
                          <span className="text-xs" style={{ color: C.text2 }}>{other?.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {t.reminderIds.length > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs" style={{ color: C.text2 }}>
                            <Bell size={12} strokeWidth={1.5} style={{ color: C.gold }} />
                            <span className="wcc-num">{t.reminderIds.length}</span>
                          </span>
                        ) : <span className="text-xs" style={{ color: C.text3 }}>—</span>}
                      </td>
                      <td className="px-3 py-3">
                        <IconButton icon={MoreHorizontal} label="Actions" onClick={(e) => e.stopPropagation()} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* =========================================================================
   REMINDER BUILDER MODAL
   ========================================================================= */
const DAYS_SHORT = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function ReminderBuilderModal({ open, onClose, onSave, initial }) {
  const [type, setType] = useState(initial?.type || "daily");
  const [onceAt, setOnceAt] = useState(initial?.config?.at || iso(NOW + 4 * HOUR).slice(0,16));
  const [dailyAt, setDailyAt] = useState(initial?.config?.at || "10:00");
  const [multiTimes, setMultiTimes] = useState(initial?.config?.times || ["10:00","14:00"]);
  const [weeklyDay, setWeeklyDay] = useState(initial?.config?.day || "Mon");
  const [weeklyAt, setWeeklyAt] = useState(initial?.config?.at || "09:30");
  const [officeFrom, setOfficeFrom] = useState(initial?.config?.from || "09:30");
  const [officeTo, setOfficeTo] = useState(initial?.config?.to || "18:30");
  const [officeDays, setOfficeDays] = useState(initial?.config?.days || ["Mon","Tue","Wed","Thu","Fri","Sat"]);
  const [cron, setCron] = useState(initial?.config?.cron || "0 */2 * * *");

  const buildConfig = () => {
    if (type === "once")          return { at: new Date(onceAt).toISOString() };
    if (type === "daily")         return { at: dailyAt };
    if (type === "multi_daily")   return { times: multiTimes };
    if (type === "weekly")        return { day: weeklyDay, at: weeklyAt };
    if (type === "hourly_office") return { from: officeFrom, to: officeTo, days: officeDays };
    if (type === "custom")        return { cron };
    return {};
  };

  const preview = useMemo(() => {
    const fake = { type, config: buildConfig() };
    try { return nextFires(fake, 5); } catch { return []; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, onceAt, dailyAt, multiTimes, weeklyDay, weeklyAt, officeFrom, officeTo, officeDays, cron]);

  const toggleDay = (d) => setOfficeDays(officeDays.includes(d) ? officeDays.filter((x) => x !== d) : [...officeDays, d]);

  const updateMulti = (i, val) => setMultiTimes(multiTimes.map((t, idx) => idx === i ? val : t));
  const addMulti = () => setMultiTimes([...multiTimes, "12:00"].slice(0, 4));
  const removeMulti = (i) => setMultiTimes(multiTimes.filter((_, idx) => idx !== i));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add reminder"
      width={440}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave({ type, config: buildConfig() })}>Save reminder</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Reminder type</label>
          <select value={type} onChange={(e) => setType(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm">
            <option value="once"          style={{ background: C.bg2 }}>Once — fire at a specific time</option>
            <option value="daily"         style={{ background: C.bg2 }}>Daily — every day at a set time</option>
            <option value="multi_daily"   style={{ background: C.bg2 }}>Multiple times per day (2–4)</option>
            <option value="weekly"        style={{ background: C.bg2 }}>Weekly — one day, one time</option>
            <option value="hourly_office" style={{ background: C.bg2 }}>Hourly during office hours</option>
            <option value="custom"        style={{ background: C.bg2 }}>Custom (cron)</option>
          </select>
        </div>

        {type === "once" && (
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Fire at</label>
            <input type="datetime-local" value={onceAt.slice(0, 16)} onChange={(e) => setOnceAt(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
          </div>
        )}

        {type === "daily" && (
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Every day at</label>
            <input type="time" value={dailyAt} onChange={(e) => setDailyAt(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
          </div>
        )}

        {type === "multi_daily" && (
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Times per day</label>
            <div className="space-y-2">
              {multiTimes.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="time" value={t} onChange={(e) => updateMulti(i, e.target.value)} className="wcc-input flex-1 rounded-md px-3 py-2 text-sm" />
                  {multiTimes.length > 1 && <IconButton icon={Trash2} label="Remove" onClick={() => removeMulti(i)} />}
                </div>
              ))}
              {multiTimes.length < 4 && <Button variant="secondary" size="sm" icon={Plus} onClick={addMulti}>Add time</Button>}
            </div>
          </div>
        )}

        {type === "weekly" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Day</label>
              <select value={weeklyDay} onChange={(e) => setWeeklyDay(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm">
                {DAYS_SHORT.map((d) => <option key={d} value={d} style={{ background: C.bg2 }}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Time</label>
              <input type="time" value={weeklyAt} onChange={(e) => setWeeklyAt(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
            </div>
          </div>
        )}

        {type === "hourly_office" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>From</label>
                <input type="time" value={officeFrom} onChange={(e) => setOfficeFrom(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>To</label>
                <input type="time" value={officeTo} onChange={(e) => setOfficeTo(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Working days</label>
              <div className="flex flex-wrap gap-1.5">
                {DAYS_SHORT.map((d) => {
                  const on = officeDays.includes(d);
                  return (
                    <button
                      key={d}
                      onClick={() => toggleDay(d)}
                      className="wcc-btn rounded-full px-3 py-1 text-xs"
                      style={{
                        background: on ? "rgba(212,175,55,0.15)" : C.bg,
                        color: on ? C.gold : C.text2,
                        border: `1px solid ${on ? "rgba(212,175,55,0.4)" : C.border}`,
                      }}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {type === "custom" && (
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Cron expression</label>
            <input value={cron} onChange={(e) => setCron(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm wcc-num" placeholder="0 */2 * * *" />
            <div className="text-xs mt-1.5 flex items-center gap-1" style={{ color: C.text3 }}>
              <Info size={12} strokeWidth={1.5} /> Example: <span className="wcc-num">0 */2 * * *</span> — every 2 hours on the hour.
            </div>
          </div>
        )}

        <div className="rounded-lg p-3" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
          <div className="text-xs mb-2 flex items-center gap-1" style={{ color: C.text3 }}>
            <Zap size={12} strokeWidth={1.5} style={{ color: C.gold }} /> Next 5 fires
          </div>
          {preview.length === 0 ? (
            <div className="text-xs" style={{ color: C.text3 }}>Configure the reminder to see a preview.</div>
          ) : (
            <ol className="space-y-1">
              {preview.map((p, i) => (
                <li key={i} className="flex items-center gap-2 text-xs wcc-num" style={{ color: C.text2 }}>
                  <span style={{ color: C.text3 }}>{i + 1}.</span> {p}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </Modal>
  );
}

/* =========================================================================
   NEW TASK MODAL
   ========================================================================= */
function NewTaskModal({ open, onClose, onCreate, users, viewerId, projects = [] }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignee, setAssignee] = useState(viewerId);
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState(iso(NOW + 3 * DAY).slice(0, 16));
  const [category, setCategory] = useState("Sales");
  const [tags, setTags] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [reminderModal, setReminderModal] = useState(false);
  const [projectId, setProjectId] = useState("");
  const [watcherIds, setWatcherIds] = useState([]);
  const [watcherQuery, setWatcherQuery] = useState("");
  const [stagedFiles, setStagedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const reset = () => {
    setTitle(""); setDescription(""); setAssignee(viewerId); setPriority("medium");
    setDueDate(iso(NOW + 3 * DAY).slice(0,16)); setCategory("Sales"); setTags([]);
    setReminders([]); setProjectId(""); setWatcherIds([]); setWatcherQuery(""); setStagedFiles([]);
  };


  const submit = async (asDraft) => {
    if (!title.trim()) return;
    setUploading(true);
    try {
      // onCreate resolves with the created task once it's saved on the server —
      // only then does it have an id, which attachments need to link to.
      const createdTask = await onCreate({
        title: title.trim(),
        description,
        assignedTo: assignee,
        priority,
        dueDate: new Date(dueDate).toISOString(),
        category,
        tags,
        status: asDraft ? "draft" : "assigned",
        reminders,
        projectId: projectId || null,
        watcherIds,
      });

      if (createdTask?.id && stagedFiles.length) {
        for (const file of stagedFiles) {
          const formData = new FormData();
          formData.append("file", file);
          try {
            await api.post(`/tasks/${createdTask.id}/attachments`, formData);
          } catch (err) {
            // One failed file shouldn't block the others or hide that the task was created.
            console.error(`Attachment "${file.name}" failed to upload:`, err?.response?.data?.error || err.message);
          }
        }
      }
      // Only clear and close once the task actually saved successfully.
      reset();
      onClose();
    } catch (err) {
      // Task creation itself failed (the render-call's onCreate already showed a
      // toast via syncFail) — keep the modal open with what the user typed so
      // nothing is lost, and let them retry.
    } finally {
      setUploading(false);
    }
  };

  const MAX_ATTACH_MB = 15;
  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const tooBig = incoming.filter((f) => f.size > MAX_ATTACH_MB * 1024 * 1024);
    const ok = incoming.filter((f) => f.size <= MAX_ATTACH_MB * 1024 * 1024);
    if (tooBig.length) window.alert(`${tooBig.map((f) => f.name).join(", ")} exceeds the ${MAX_ATTACH_MB}MB limit and was skipped.`);
    setStagedFiles((prev) => [...prev, ...ok]);
  };
  const removeStagedFile = (idx) => setStagedFiles((prev) => prev.filter((_, i) => i !== idx));
  const formatBytes = (n) => n < 1024 * 1024 ? `${Math.round(n / 1024)}KB` : `${(n / (1024 * 1024)).toFixed(1)}MB`;

  return (
    <>
      <Modal
        open={open && !reminderModal}
        onClose={onClose}
        title="New task"
        width={640}
        footer={
          <>
            <Button variant="secondary" onClick={() => submit(true)} disabled={uploading}>Save as draft</Button>
            <Button onClick={() => submit(false)} disabled={!title.trim() || uploading}>
              {uploading ? "Creating…" : "Assign now"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Title *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Quarterly portfolio review — Iyer HUF" className="wcc-input w-full rounded-md px-3 py-2 text-sm" autoFocus />
          </div>
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Context, links, references…" className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Assign to</label>
              <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm">
                {users.map((u) => <option key={u.id} value={u.id} style={{ background: C.bg2 }}>{u.name} — {u.role}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Category</label>
              <CategorySelect value={category} onChange={setCategory} />
            </div>
          </div>

          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Project (optional)</label>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm">
              <option value="" style={{ background: C.bg2 }}>— No project</option>
              {projects.filter((p) => p.status === "active").map((p) => <option key={p.id} value={p.id} style={{ background: C.bg2 }}>{p.name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Tag people (watchers) — they'll be notified</label>

            {/* Selected watchers as removable chips */}
            {watcherIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {watcherIds.map((id) => {
                  const u = users.find((x) => x.id === id);
                  if (!u) return null;
                  return (
                    <span key={id} className="text-xs rounded-full px-2.5 py-1 flex items-center gap-1.5"
                      style={{ background: "rgba(212,175,55,0.15)", border: `1px solid ${C.gold}`, color: C.gold }}>
                      {u.name}
                      <button type="button" onClick={() => setWatcherIds(watcherIds.filter((x) => x !== id))}
                        style={{ display: "inline-flex" }} aria-label={`Remove ${u.name}`}>
                        <X size={11} strokeWidth={2} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Type-to-search field */}
            <div className="relative">
              <input
                value={watcherQuery}
                onChange={(e) => setWatcherQuery(e.target.value)}
                placeholder="Type a name to tag…"
                className="wcc-input w-full rounded-md px-3 py-2 text-sm"
              />
              {watcherQuery.trim() && (
                <div className="absolute z-10 mt-1 w-full rounded-md overflow-hidden" style={{ background: C.bg2, border: `1px solid ${C.border}`, maxHeight: 180, overflowY: "auto" }}>
                  {users
                    .filter((u) => u.id !== assignee && !watcherIds.includes(u.id) && u.name.toLowerCase().includes(watcherQuery.trim().toLowerCase()))
                    .slice(0, 6)
                    .map((u) => (
                      <button key={u.id} type="button"
                        onClick={() => { setWatcherIds([...watcherIds, u.id]); setWatcherQuery(""); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm"
                        onMouseEnter={(e) => e.currentTarget.style.background = C.bg3}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                        <Avatar user={u} size={22} />
                        <span className="flex-1">{u.name}</span>
                        <span className="text-xs" style={{ color: C.text3 }}>{u.role}</span>
                      </button>
                    ))}
                  {users.filter((u) => u.id !== assignee && !watcherIds.includes(u.id) && u.name.toLowerCase().includes(watcherQuery.trim().toLowerCase())).length === 0 && (
                    <div className="px-3 py-2 text-xs" style={{ color: C.text3 }}>No matches</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Attachments</label>
            <label
              className="wcc-btn flex items-center justify-center gap-2 rounded-md px-3 py-3 text-sm cursor-pointer"
              style={{ background: C.bg, border: `1px dashed ${C.border}`, color: C.text2 }}
            >
              <Paperclip size={14} strokeWidth={1.5} />
              Click to add files — screenshots, PDFs, PPTs, docs (max 15MB each)
              <input
                type="file"
                multiple
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                style={{ display: "none" }}
              />
            </label>
            {stagedFiles.length > 0 && (
              <div className="space-y-1.5 mt-2">
                {stagedFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-md px-3 py-1.5 text-xs" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
                    <Paperclip size={12} strokeWidth={1.5} style={{ color: C.text3, flexShrink: 0 }} />
                    <span className="flex-1 truncate" style={{ color: C.text2 }}>{f.name}</span>
                    <span style={{ color: C.text3 }}>{formatBytes(f.size)}</span>
                    <button type="button" onClick={() => removeStagedFile(i)} className="wcc-btn" style={{ color: C.text3 }}>
                      <X size={13} strokeWidth={2} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Priority</label>
            <div className="flex gap-2">
              {PRIORITIES.map((p) => {
                const on = priority === p;
                const tones = {
                  low:    { bg: on ? C.bg3 : C.bg,                 fg: on ? C.text : C.text3, border: on ? C.borderHover : C.border },
                  medium: { bg: on ? "rgba(59,130,246,0.15)" : C.bg,  fg: on ? C.info : C.text3, border: on ? "rgba(59,130,246,0.4)" : C.border },
                  high:   { bg: on ? "rgba(245,158,11,0.15)" : C.bg,  fg: on ? C.warning : C.text3, border: on ? "rgba(245,158,11,0.4)" : C.border },
                  urgent: { bg: on ? "rgba(239,68,68,0.15)" : C.bg,   fg: on ? C.danger : C.text3, border: on ? "rgba(239,68,68,0.4)" : C.border },
                };
                const t = tones[p];
                return (
                  <button key={p} onClick={() => setPriority(p)} className="wcc-btn rounded-full px-3 py-1 text-xs font-medium capitalize"
                    style={{ background: t.bg, color: t.fg, border: `1px solid ${t.border}` }}>
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Due date & time</label>
              <input type="datetime-local" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs block" style={{ color: C.text3 }}>Reminders</label>
              <Button variant="secondary" size="sm" icon={Plus} onClick={() => setReminderModal(true)}>Add reminder</Button>
            </div>
            {reminders.length === 0 ? (
              <div className="text-xs rounded-lg p-3" style={{ color: C.text3, background: C.bg, border: `1px dashed ${C.border}` }}>
                No reminders yet. Add hourly, daily, weekly or custom reminders — this task can nudge itself.
              </div>
            ) : (
              <div className="space-y-1.5">
                {reminders.map((r, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md px-3 py-2 text-xs" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
                    <div className="flex items-center gap-2">
                      <Bell size={13} strokeWidth={1.5} style={{ color: C.gold }} />
                      <span style={{ color: C.text }}>{reminderLabel(r)}</span>
                    </div>
                    <IconButton icon={Trash2} label="Remove" onClick={() => setReminders(reminders.filter((_, idx) => idx !== i))} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>
      <ReminderBuilderModal
        open={reminderModal}
        onClose={() => setReminderModal(false)}
        onSave={(r) => { setReminders([...reminders, r]); setReminderModal(false); }}
      />
    </>
  );
}

/* =========================================================================
   EDIT TASK MODAL
   ========================================================================= */
function EditTaskModal({ open, onClose, onSave, task, users = [] }) {
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState("");
  const [category, setCategory] = useState("Sales");
  const [tags, setTags] = useState([]);
  const [watcherIds, setWatcherIds] = useState([]);
  const [watcherQuery, setWatcherQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (task && open) {
      setDescription(task.description || "");
      setPriority(task.priority);
      setDueDate(new Date(task.dueDate).toISOString().slice(0, 16));
      setCategory(task.category);
      setTags(task.tags || []);
      setWatcherIds(task.watcherIds || []);
      setWatcherQuery("");
      setSaving(false);
      setError("");
    }
  }, [task, open]);

  if (!task) return null;


  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      await onSave({
        description,
        priority,
        dueDate: new Date(dueDate).toISOString(),
        category,
        tags,
        watcherIds,
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Couldn't save changes — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit task"
      width={560}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="text-xs rounded-md px-3 py-2" style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text3 }}>
          Editing <span className="wcc-h" style={{ color: C.text }}>{task.title}</span>. To rename it, use the title field in the drawer. To reassign it, use "Reassign".
        </div>

        <div>
          <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Context, links, references…" className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Category</label>
            <CategorySelect value={category} onChange={setCategory} />
          </div>
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Due date & time</label>
            <input type="datetime-local" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
          </div>
        </div>

        <div>
          <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Tag people (watchers) — they'll be notified</label>

          {watcherIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {watcherIds.map((id) => {
                const u = users.find((x) => x.id === id);
                if (!u) return null;
                return (
                  <span key={id} className="text-xs rounded-full px-2.5 py-1 flex items-center gap-1.5"
                    style={{ background: "rgba(212,175,55,0.15)", border: `1px solid ${C.gold}`, color: C.gold }}>
                    {u.name}
                    <button type="button" onClick={() => setWatcherIds(watcherIds.filter((x) => x !== id))}
                      style={{ display: "inline-flex" }} aria-label={`Remove ${u.name}`}>
                      <X size={11} strokeWidth={2} />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          <div className="relative">
            <input
              value={watcherQuery}
              onChange={(e) => setWatcherQuery(e.target.value)}
              placeholder="Type a name to tag…"
              className="wcc-input w-full rounded-md px-3 py-2 text-sm"
            />
            {watcherQuery.trim() && (
              <div className="absolute z-10 mt-1 w-full rounded-md overflow-hidden" style={{ background: C.bg2, border: `1px solid ${C.border}`, maxHeight: 180, overflowY: "auto" }}>
                {users
                  .filter((u) => u.id !== task.assignedTo && !watcherIds.includes(u.id) && u.name.toLowerCase().includes(watcherQuery.trim().toLowerCase()))
                  .slice(0, 6)
                  .map((u) => (
                    <button key={u.id} type="button"
                      onClick={() => { setWatcherIds([...watcherIds, u.id]); setWatcherQuery(""); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm"
                      onMouseEnter={(e) => e.currentTarget.style.background = C.bg3}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                      <Avatar user={u} size={22} />
                      <span className="flex-1">{u.name}</span>
                      <span className="text-xs" style={{ color: C.text3 }}>{u.role}</span>
                    </button>
                  ))}
                {users.filter((u) => u.id !== task.assignedTo && !watcherIds.includes(u.id) && u.name.toLowerCase().includes(watcherQuery.trim().toLowerCase())).length === 0 && (
                  <div className="px-3 py-2 text-xs" style={{ color: C.text3 }}>No matches</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Priority</label>
          <div className="flex gap-2">
            {PRIORITIES.map((p) => {
              const on = priority === p;
              const tones = {
                low:    { bg: on ? C.bg3 : C.bg,                 fg: on ? C.text : C.text3, border: on ? C.borderHover : C.border },
                medium: { bg: on ? "rgba(59,130,246,0.15)" : C.bg,  fg: on ? C.info : C.text3, border: on ? "rgba(59,130,246,0.4)" : C.border },
                high:   { bg: on ? "rgba(245,158,11,0.15)" : C.bg,  fg: on ? C.warning : C.text3, border: on ? "rgba(245,158,11,0.4)" : C.border },
                urgent: { bg: on ? "rgba(239,68,68,0.15)" : C.bg,   fg: on ? C.danger : C.text3, border: on ? "rgba(239,68,68,0.4)" : C.border },
              };
              const t = tones[p];
              return (
                <button key={p} onClick={() => setPriority(p)} className="wcc-btn rounded-full px-3 py-1 text-xs font-medium capitalize"
                  style={{ background: t.bg, color: t.fg, border: `1px solid ${t.border}` }}>
                  {p}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="text-xs px-3 py-2 rounded-md" style={{ background: "rgba(239,68,68,0.12)", color: C.danger, border: `1px solid rgba(239,68,68,0.3)` }}>
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

/* =========================================================================
   TASK DETAIL DRAWER
   ========================================================================= */
function TaskDetailDrawer({ open, onClose, task, users, tasks, viewer, comments, activity, reminders, onAction, onAddComment, onAddReminder, onEditTask, onOpenTask, onCreateSubtask }) {
  const [reminderModal, setReminderModal] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");
  const [editTitle, setEditTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [mentionOpen, setMentionOpen] = useState(false);
  const [subtaskDraft, setSubtaskDraft] = useState("");
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachBusy, setAttachBusy] = useState(false);

  useEffect(() => {
    if (task) { setTitleDraft(task.title); setShowLog(false); setCommentDraft(""); setEditTitle(false); setEditModalOpen(false); }
  }, [task?.id]);

  useEffect(() => {
    if (!task) { setAttachments([]); return; }
    api.get(`/tasks/${task.id}/attachments`)
      .then(({ data }) => setAttachments(data))
      .catch(() => setAttachments([])); // e.g. attachments feature not installed on this server — fail quietly
  }, [task?.id]);

  if (!open || !task) return null;

  const assignee = userMap[task.assignedTo];
  const creator = userMap[task.createdBy];
  const helper = task.currentHelperId ? userMap[task.currentHelperId] : null;
  const isAssignee = viewer.id === task.assignedTo;
  const isHelper = viewer.id === task.currentHelperId;
  const above = isAbove(viewer, assignee, users);
  const canEdit = isAssignee || viewer.id === task.createdBy || above;
  const canReassign = above;
  const canClose = roleRank[viewer.role] >= roleRank["Manager"] && task.status === "completed";
  // Permanent delete: the person who created the task, or an Admin/Director.
  const canDelete = viewer.id === task.createdBy || canManageAll(viewer);

  const handleAttachFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setAttachBusy(true);
    for (const file of files) {
      if (file.size > 15 * 1024 * 1024) {
        window.alert(`${file.name} exceeds the 15MB limit and was skipped.`);
        continue;
      }
      const formData = new FormData();
      formData.append("file", file);
      try {
        const { data } = await api.post(`/tasks/${task.id}/attachments`, formData);
        setAttachments((prev) => [data, ...prev]);
      } catch (err) {
        window.alert(err?.response?.data?.error || `Could not upload ${file.name}.`);
      }
    }
    setAttachBusy(false);
  };

  const handleDownloadAttachment = async (att) => {
    try {
      const res = await api.get(`/tasks/${task.id}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = att.originalName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      window.alert("Could not download this file.");
    }
  };

  const handleDeleteAttachment = async (att) => {
    if (!window.confirm(`Delete "${att.originalName}"?`)) return;
    try {
      await api.delete(`/tasks/${task.id}/attachments/${att.id}`);
      setAttachments((prev) => prev.filter((a) => a.id !== att.id));
    } catch (err) {
      window.alert(err?.response?.data?.error || "Could not delete this file.");
    }
  };

  const taskComments = comments.filter((c) => c.taskId === task.id).sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt));
  const taskActivity = activity.filter((a) => a.taskId === task.id).sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 20);
  const taskReminders = reminders.filter((r) => r.taskId === task.id && r.active);
  const subtasks = (tasks || []).filter((t) => t.parentTaskId === task.id);
  const subtasksDone = subtasks.filter((t) => t.status === "completed" || t.status === "closed").length;

  const actionIconFor = (act) => {
    const map = {
      created: Plus, assigned: Send, reassigned: Repeat, status_changed: PlayCircle,
      help_requested: HandHelping, help_accepted: Check, help_completed: CheckCircle2,
      commented: MessageSquare, closed: ShieldCheck, reminder_added: Bell, reminder_edited: Edit3, edited: Edit3,
    };
    return map[act] || CircleDot;
  };

  const commentBox = (
    <div className="rounded-lg p-2" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
      <textarea
        value={commentDraft}
        onChange={(e) => { setCommentDraft(e.target.value); setMentionOpen(e.target.value.endsWith("@")); }}
        placeholder="Add a comment. Use @ to mention…"
        rows={2}
        className="w-full bg-transparent text-sm outline-none resize-none"
        style={{ color: C.text }}
      />
      {mentionOpen && (
        <div className="rounded-md mb-2 max-h-40 overflow-y-auto wcc-scroll" style={{ background: C.bg3, border: `1px solid ${C.border}` }}>
          {users.slice(0, 5).map((u) => (
            <button
              key={u.id}
              onClick={() => { setCommentDraft(commentDraft + u.name.split(" ")[0] + " "); setMentionOpen(false); }}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left wcc-btn"
              onMouseEnter={(e) => e.currentTarget.style.background = C.bg}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <Avatar user={u} size={22} />
              <span style={{ color: C.text }}>{u.name}</span>
              <span style={{ color: C.text3 }}>· {u.role}</span>
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-end gap-2 pt-2" style={{ borderTop: `1px solid ${C.border}` }}>
        <Button variant="ghost" size="sm" onClick={() => setCommentDraft("")}>Clear</Button>
        <Button size="sm" onClick={() => { if (commentDraft.trim()) { onAddComment(task.id, commentDraft.trim()); setCommentDraft(""); } }} disabled={!commentDraft.trim()}>Post</Button>
      </div>
    </div>
  );

  return (
    <>
      <Drawer open={open} onClose={onClose} width={620}>
        {/* Header */}
        <div className="p-5 sticky top-0 z-10" style={{ background: C.bg2, borderBottom: `1px solid ${C.border}` }}>
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              {editTitle && canEdit ? (
                <div className="flex items-center gap-2">
                  <input value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} className="wcc-input flex-1 rounded-md px-3 py-1.5 text-sm" autoFocus />
                  <Button size="sm" onClick={() => { onAction("edit_title", { title: titleDraft }); setEditTitle(false); }}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setTitleDraft(task.title); setEditTitle(false); }}>Cancel</Button>
                </div>
              ) : (
                <div className="wcc-h text-lg pr-6 cursor-text" onClick={() => canEdit && setEditTitle(true)} style={{ letterSpacing: "-0.01em" }}>
                  {task.title}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-1.5 mt-3">
                <StatusPill status={task.status} />
                <PriorityPill priority={task.priority} />
                <Pill tone="neutral">{task.category}</Pill>
                {task.tags.map((t) => <Pill key={t} tone="muted">#{t}</Pill>)}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {canReassign && <Button size="sm" variant="secondary" icon={Repeat} onClick={() => onAction("reassign")}>Reassign</Button>}
              <Dropdown
                align="right"
                width={180}
                trigger={<IconButton icon={MoreHorizontal} label="More" />}
              >
                {(close) => (
                  <div className="p-1">
                    {canEdit && (
                      <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left wcc-btn" onClick={() => { setEditModalOpen(true); close(); }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#E5E7EB"; e.currentTarget.style.color = "#0A1628"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = ""; }}>
                        <Edit3 size={14} strokeWidth={1.5} /> Edit details
                      </button>
                    )}
                    <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left wcc-btn" onClick={() => { onAction("duplicate"); close(); }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#E5E7EB"; e.currentTarget.style.color = "#0A1628"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = ""; }}>
                      <Copy size={14} strokeWidth={1.5} /> Duplicate
                    </button>
                    {canDelete && (
                      <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left wcc-btn" onClick={() => { onAction("delete_task"); close(); }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#E5E7EB"; e.currentTarget.style.color = "#DC2626"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#F87171"; }} style={{ color: "#F87171" }}>
                        <Trash2 size={14} strokeWidth={1.5} /> Delete task
                      </button>
                    )}
                    {canClose && (
                      <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left wcc-btn" onClick={() => { onAction("close_task"); close(); }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#E5E7EB"; e.currentTarget.style.color = "#8A6D1F"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.gold; }} style={{ color: C.gold }}>
                        <ShieldCheck size={14} strokeWidth={1.5} /> Close task
                      </button>
                    )}
                  </div>
                )}
              </Dropdown>
              <IconButton icon={X} label="Close" onClick={onClose} />
            </div>
          </div>
        </div>

        {/* Meta grid */}
        <div className="p-5 grid grid-cols-2 gap-4" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div className="text-xs mb-1.5" style={{ color: C.text3 }}>Assigned to</div>
            <div className="flex items-center gap-2">
              <Avatar user={assignee} size={28} />
              <div>
                <div className="text-sm">{assignee.name}</div>
                <div className="text-xs" style={{ color: C.text3 }}>{assignee.role}</div>
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs mb-1.5" style={{ color: C.text3 }}>Created by</div>
            <div className="flex items-center gap-2">
              <Avatar user={creator} size={28} />
              <div>
                <div className="text-sm">{creator.name}</div>
                <div className="text-xs" style={{ color: C.text3 }}>{creator.role}</div>
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs mb-1.5" style={{ color: C.text3 }}>Due date</div>
            <div className="flex items-center gap-2">
              <Calendar size={14} strokeWidth={1.5} style={{ color: isOverdue(task) ? C.danger : C.text2 }} />
              <div className="text-sm wcc-num" style={{ color: isOverdue(task) ? C.danger : C.text }}>
                {absDate(task.dueDate)}
              </div>
              {isOverdue(task) && <Pill tone="danger" dot>Overdue {relTime(task.dueDate)}</Pill>}
              {!isOverdue(task) && <span className="text-xs" style={{ color: C.text3 }}>{relTime(task.dueDate)}</span>}
            </div>
          </div>
          <div>
            <div className="text-xs mb-1.5" style={{ color: C.text3 }}>Created</div>
            <div className="text-sm wcc-num" style={{ color: C.text2 }}>{fmtDate(task.createdAt)}</div>
          </div>
        </div>

        {/* Helper info */}
        {helper && (
          <div className="mx-5 mt-4 rounded-lg p-3 flex items-center gap-3" style={{ background: "rgba(245,158,11,0.08)", border: `1px solid rgba(245,158,11,0.25)` }}>
            <HandHelping size={16} strokeWidth={1.5} style={{ color: C.warning }} />
            <div className="text-xs flex-1">
              <span style={{ color: C.text2 }}>Helper: </span>
              <span className="wcc-h" style={{ color: C.text }}>{helper.name}</span>
              <span style={{ color: C.text3 }}> — {task.status === "help_in_progress" ? "supporting the task" : "pending acceptance"}</span>
            </div>
          </div>
        )}

        {/* Description */}
        <div className="p-5" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="text-xs mb-2" style={{ color: C.text3 }}>Description</div>
          <div className="text-sm leading-relaxed" style={{ color: C.text }}>{task.description || <span style={{ color: C.text3 }}>No description added.</span>}</div>
        </div>

        {/* Reminders */}
        <div className="p-5" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="flex items-center justify-between mb-3">
            <div className="wcc-h text-sm flex items-center gap-2">
              <Bell size={14} strokeWidth={1.5} style={{ color: C.gold }} /> Reminders
              <Pill tone="neutral">{taskReminders.length}</Pill>
            </div>
            <Button variant="secondary" size="sm" icon={Plus} onClick={() => setReminderModal(true)}>Add reminder</Button>
          </div>
          {taskReminders.length === 0 ? (
            <div className="text-xs rounded-lg p-3" style={{ color: C.text3, background: C.bg, border: `1px dashed ${C.border}` }}>
              No reminders set. Add one to nudge the assignee automatically.
            </div>
          ) : (
            <div className="space-y-1.5">
              {taskReminders.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-md px-3 py-2 text-xs" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Bell size={13} strokeWidth={1.5} style={{ color: C.gold }} />
                    <div className="min-w-0">
                      <div className="truncate" style={{ color: C.text }}>{reminderLabel(r)}</div>
                      <div className="wcc-num" style={{ color: C.text3 }}>Next: {absDate(r.nextFireAt)}</div>
                    </div>
                  </div>
                  <IconButton icon={Trash2} label="Remove reminder" onClick={() => onAction("delete_reminder", { reminderId: r.id })} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action row — context-aware */}
        <div className="p-5" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="text-xs mb-2" style={{ color: C.text3 }}>Actions</div>
          <div className="flex flex-wrap gap-2">
            {isAssignee && ["assigned"].includes(task.status) && (
              <>
                <Button icon={PlayCircle} onClick={() => onAction("start")}>Mark in progress</Button>
                <Button variant="secondary" icon={CheckCircle2} onClick={() => onAction("complete")}>Mark complete</Button>
                <Button variant="secondary" icon={HandHelping} onClick={() => onAction("ask_help")}>Ask for help</Button>
              </>
            )}
            {isAssignee && task.status === "in_progress" && (
              <>
                <Button icon={CheckCircle2} onClick={() => onAction("complete")}>Mark complete</Button>
                <Button variant="secondary" icon={HandHelping} onClick={() => onAction("ask_help")}>Ask for help</Button>
              </>
            )}
            {isAssignee && task.status === "awaiting_help" && helper && (
              <Button variant="secondary" disabled className="italic">Waiting on {helper.name.split(" ")[0]}…</Button>
            )}
            {isAssignee && task.status === "help_in_progress" && helper && (
              <Button variant="secondary" disabled className="italic">Waiting on {helper.name.split(" ")[0]}…</Button>
            )}
            {isHelper && task.status === "awaiting_help" && (
              <>
                <Button icon={Check} onClick={() => onAction("accept_help")}>Accept help request</Button>
                <Button variant="secondary" icon={X} onClick={() => onAction("decline_help")}>Decline</Button>
              </>
            )}
            {isHelper && task.status === "help_in_progress" && (
              <Button icon={CheckCircle2} onClick={() => onAction("complete_help")}>Mark my help complete</Button>
            )}
            {canClose && (
              <Button icon={ShieldCheck} onClick={() => onAction("close_task")}>Close task</Button>
            )}
            {!isAssignee && !isHelper && !canClose && task.status !== "closed" && (
              <div className="text-xs" style={{ color: C.text3 }}>You're viewing this task. Actions available to the assignee, helper, or hierarchy above them.</div>
            )}
            {task.status === "closed" && (
              <Pill tone="success" dot>Closed{task.closedAt ? ` · ${relTime(task.closedAt)}` : ""}</Pill>
            )}
          </div>
        </div>

        {/* Sub-tasks */}
        <div className="p-5" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="wcc-h text-sm mb-3 flex items-center gap-2">
            <ListChecks size={14} strokeWidth={1.5} /> Sub-tasks
            {subtasks.length > 0 && <Pill tone="neutral">{subtasksDone}/{subtasks.length}</Pill>}
          </div>

          {subtasks.length === 0 && !addingSubtask && (
            <div className="text-xs mb-3" style={{ color: C.text3 }}>No sub-tasks yet — break this down into smaller pieces if it helps.</div>
          )}

          {subtasks.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {subtasks.map((st) => {
                const stAssignee = userMap[st.assignedTo];
                const done = st.status === "completed" || st.status === "closed";
                return (
                  <div key={st.id} className="wcc-row cursor-pointer rounded-md px-3 py-2 flex items-center gap-2"
                    style={{ background: C.bg, border: `1px solid ${C.border}` }}
                    onClick={() => onOpenTask && onOpenTask(st.id)}>
                    {done
                      ? <CheckCircle2 size={15} strokeWidth={1.5} style={{ color: C.success, flexShrink: 0 }} />
                      : <CircleDot size={15} strokeWidth={1.5} style={{ color: C.text3, flexShrink: 0 }} />}
                    <span className="text-sm flex-1 truncate" style={{ color: done ? C.text3 : C.text, textDecoration: done ? "line-through" : "none" }}>
                      {st.title}
                    </span>
                    {stAssignee && <Avatar user={stAssignee} size={20} />}
                    <StatusPill status={st.status} />
                  </div>
                );
              })}
            </div>
          )}

          {addingSubtask ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={subtaskDraft}
                onChange={(e) => setSubtaskDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && subtaskDraft.trim()) {
                    onCreateSubtask && onCreateSubtask(task, subtaskDraft.trim());
                    setSubtaskDraft("");
                  }
                  if (e.key === "Escape") { setAddingSubtask(false); setSubtaskDraft(""); }
                }}
                placeholder="Sub-task title, then Enter…"
                className="wcc-input flex-1 rounded-md px-3 py-2 text-sm"
              />
              <Button size="sm" onClick={() => {
                if (subtaskDraft.trim()) { onCreateSubtask && onCreateSubtask(task, subtaskDraft.trim()); setSubtaskDraft(""); }
              }}>Add</Button>
              <Button size="sm" variant="ghost" onClick={() => { setAddingSubtask(false); setSubtaskDraft(""); }}>Cancel</Button>
            </div>
          ) : (
            <button className="wcc-btn text-xs flex items-center gap-1.5" style={{ color: C.gold }} onClick={() => setAddingSubtask(true)}>
              <Plus size={13} strokeWidth={2} /> Add sub-task
            </button>
          )}
        </div>

        {/* Attachments */}
        <div className="p-5" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="wcc-h text-sm mb-3 flex items-center gap-2">
            <Paperclip size={14} strokeWidth={1.5} /> Attachments
            {attachments.length > 0 && <Pill tone="neutral">{attachments.length}</Pill>}
          </div>

          {attachments.length === 0 && (
            <div className="text-xs mb-3" style={{ color: C.text3 }}>No files attached yet.</div>
          )}

          {attachments.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {attachments.map((att) => {
                const uploader = userMap[att.uploadedBy];
                const canRemove = att.uploadedBy === viewer.id || canManageAll(viewer);
                return (
                  <div key={att.id} className="rounded-md px-3 py-2 flex items-center gap-2"
                    style={{ background: C.bg, border: `1px solid ${C.border}` }}>
                    <Paperclip size={13} strokeWidth={1.5} style={{ color: C.text3, flexShrink: 0 }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{att.originalName}</div>
                      <div className="text-xs" style={{ color: C.text3 }}>
                        {uploader ? uploader.name : "Unknown user"} · {(att.size / 1024).toFixed(0)}KB
                      </div>
                    </div>
                    <button onClick={() => handleDownloadAttachment(att)} className="wcc-btn" style={{ color: C.gold }} aria-label="Download">
                      <Download size={14} strokeWidth={1.5} />
                    </button>
                    {canRemove && (
                      <button onClick={() => handleDeleteAttachment(att)} className="wcc-btn" style={{ color: "#F87171" }} aria-label="Delete">
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <label className="wcc-btn flex items-center justify-center gap-2 rounded-md px-3 py-2 text-xs cursor-pointer" style={{ background: C.bg, border: `1px dashed ${C.border}`, color: C.text3, opacity: attachBusy ? 0.6 : 1 }}>
            {attachBusy ? "Uploading…" : "Click to add files (max 15MB each)"}
            <input type="file" multiple onChange={(e) => { handleAttachFiles(e.target.files); e.target.value = ""; }} disabled={attachBusy} style={{ display: "none" }} />
          </label>
        </div>

        {/* Comments */}
        <div className="p-5" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="wcc-h text-sm mb-3 flex items-center gap-2">
            <MessageSquare size={14} strokeWidth={1.5} /> Comments
            <Pill tone="neutral">{taskComments.length}</Pill>
          </div>
          <div className="space-y-3 mb-3">
            {taskComments.length === 0 && (
              <div className="text-xs" style={{ color: C.text3 }}>No comments yet — start the thread below.</div>
            )}
            {taskComments.map((c) => {
              const u = userMap[c.userId] || { name: "Unknown user", avatarInitials: "?" };
              return (
                <div key={c.id} className="flex gap-3">
                  <Avatar user={u} size={30} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm wcc-h">{u.name}</span>
                      <span className="text-xs" style={{ color: C.text3 }} title={absDate(c.createdAt)}>{relTime(c.createdAt)}</span>
                    </div>
                    <div className="text-sm mt-0.5" style={{ color: C.text }}>{c.text}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {commentBox}
        </div>

        {/* Activity log */}
        <div className="p-5">
          <button className="wcc-btn w-full flex items-center justify-between text-sm wcc-h" onClick={() => setShowLog(!showLog)} style={{ color: C.text }}>
            <span className="flex items-center gap-2"><History size={14} strokeWidth={1.5} /> Activity log <Pill tone="neutral">{taskActivity.length}</Pill></span>
            <ChevronDown size={16} strokeWidth={1.5} style={{ color: C.text3, transform: showLog ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />
          </button>
          {showLog && (
            <div className="mt-3 space-y-2">
              {taskActivity.map((a) => {
                const Icon = actionIconFor(a.action);
                const u = userMap[a.userId] || { name: "Unknown user", avatarInitials: "?" };
                return (
                  <div key={a.id} className="flex gap-3 items-start text-xs">
                    <div className="rounded-full p-1.5 flex-shrink-0" style={{ background: C.bg3 }}>
                      <Icon size={12} strokeWidth={1.5} style={{ color: C.gold }} />
                    </div>
                    <div className="flex-1 min-w-0 py-0.5">
                      <div style={{ color: C.text2 }}>
                        <span className="wcc-h" style={{ color: C.text }}>{u?.name}</span> · {a.details}
                      </div>
                      <div style={{ color: C.text3 }}>{relTime(a.timestamp)} · {absDate(a.timestamp)}</div>
                    </div>
                  </div>
                );
              })}
              <div className="text-xs mt-3 pt-3 flex items-center gap-1" style={{ color: C.text3, borderTop: `1px solid ${C.border}` }}>
                <ShieldCheck size={11} strokeWidth={1.5} /> All activity is permanently logged for compliance.
              </div>
            </div>
          )}
        </div>
      </Drawer>
      <ReminderBuilderModal
        open={reminderModal}
        onClose={() => setReminderModal(false)}
        onSave={(r) => { onAddReminder(task.id, r); setReminderModal(false); }}
      />
      <EditTaskModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        task={task}
        users={users}
        onSave={(data) => onEditTask(task.id, data)}
      />
    </>
  );
}

/* =========================================================================
   HELP REQUESTS SCREEN
   ========================================================================= */
function HelpRequestsScreen({ viewer, helpRequests, tasks, users, onOpenTask }) {
  const [tab, setTab] = useState("received");
  const received = helpRequests.filter((h) => h.helperUserId === viewer.id);
  const made     = helpRequests.filter((h) => h.requestedBy === viewer.id);
  const rows = tab === "received" ? received : made;

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div>
        <div className="wcc-h text-xl">Help Requests</div>
        <div className="text-sm mt-0.5" style={{ color: C.text3 }}>
          When an assignee gets stuck, they can pull a colleague into the loop. Requests you're party to appear below.
        </div>
      </div>

      <div className="flex items-center gap-1">
        {[
          { key: "received", label: "Requests I received", count: received.length },
          { key: "made",     label: "Requests I made",     count: made.length },
        ].map((tabDef) => (
          <button
            key={tabDef.key}
            onClick={() => setTab(tabDef.key)}
            className="wcc-btn px-4 py-2 rounded-lg text-sm flex items-center gap-2"
            style={{
              background: tab === tabDef.key ? C.bg3 : "transparent",
              color: tab === tabDef.key ? C.text : C.text2,
              border: `1px solid ${tab === tabDef.key ? C.border : "transparent"}`,
              fontWeight: tab === tabDef.key ? 600 : 500,
            }}
          >
            {tabDef.label}
            <Pill tone={tab === tabDef.key ? "gold" : "neutral"}>{tabDef.count}</Pill>
          </button>
        ))}
      </div>

      <Card padding="p-0">
        {rows.length === 0 ? (
          <EmptyState icon={HandHelping} title={tab === "received" ? "No help requests coming your way" : "You haven't asked for help yet"} subtitle={tab === "received" ? "When teammates loop you in, you'll see them here." : "Open a task and use 'Ask for help' to bring a colleague in."} />
        ) : (
          <div className="divide-y" style={{ borderColor: C.border }}>
            {rows.map((h) => {
              const task = tasks.find((t) => t.id === h.taskId);
              const other = userMap[tab === "received" ? h.requestedBy : h.helperUserId];
              const toneMap = { pending: "warning", accepted: "info", completed: "success", declined: "muted" };
              return (
                <div key={h.id} className="wcc-row cursor-pointer px-4 py-3 flex items-center gap-3" onClick={() => onOpenTask(h.taskId)}>
                  <Avatar user={other} size={32} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{task?.title}</div>
                    <div className="text-xs mt-0.5 truncate" style={{ color: C.text3 }}>
                      {tab === "received" ? `${other.name} asked for help` : `Asked ${other.name} for help`} · {h.note}
                    </div>
                  </div>
                  <Pill tone={toneMap[h.status]} dot>{h.status}</Pill>
                  <span className="text-xs" style={{ color: C.text3 }}>{relTime(h.createdAt)}</span>
                  <ChevronRight size={14} strokeWidth={1.5} style={{ color: C.text3 }} />
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

/* =========================================================================
   TEAM SCREEN (Team Leads and above)
   ========================================================================= */
function TeamScreen({ viewer, users, tasks, onOpenTask }) {
  const reports = allReports(viewer.id, users);
  const [selected, setSelected] = useState(reports[0]?.id || viewer.id);
  const selUser = users.find((u) => u.id === selected) || viewer;
  const selTasks = tasks.filter((t) => t.assignedTo === selUser.id);
  const columns = [
    { key: "assigned",           label: "Assigned",           tone: C.info },
    { key: "in_progress",        label: "In progress",        tone: C.gold },
    { key: "help_group",         label: "Awaiting help",      tone: C.warning, match: (s) => ["awaiting_help","help_in_progress"].includes(s) },
    { key: "completed",          label: "Completed",          tone: C.success },
  ];

  const renderTree = (rootId, depth = 0) => {
    const children = users.filter((u) => u.managerId === rootId);
    if (rootId !== viewer.id && depth === 0) return null;
    return (
      <div>
        {children.map((c) => (
          <div key={c.id}>
            <button
              onClick={() => setSelected(c.id)}
              className="wcc-btn w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left mb-0.5"
              style={{
                background: selected === c.id ? "rgba(212,175,55,0.10)" : "transparent",
                paddingLeft: 12 + depth * 16,
              }}
              onMouseEnter={(e) => { if (selected !== c.id) e.currentTarget.style.background = C.bg3; }}
              onMouseLeave={(e) => { if (selected !== c.id) e.currentTarget.style.background = "transparent"; }}
            >
              <Avatar user={c} size={26} />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{c.name}</div>
                <div className="text-xs truncate" style={{ color: C.text3 }}>{c.role} · {c.department}</div>
              </div>
              <Pill tone="neutral">{tasks.filter((t) => t.assignedTo === c.id && !["closed","draft"].includes(t.status)).length}</Pill>
            </button>
            {renderTree(c.id, depth + 1)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div>
        <div className="wcc-h text-xl">Team</div>
        <div className="text-sm mt-0.5" style={{ color: C.text3 }}>Direct and indirect reports · {reports.length} people</div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card padding="p-3" className="lg:col-span-1">
          <div className="text-xs mb-2 px-1" style={{ color: C.text3 }}>YOUR REPORTS</div>
          <div>
            <button
              onClick={() => setSelected(viewer.id)}
              className="wcc-btn w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left mb-1"
              style={{ background: selected === viewer.id ? "rgba(212,175,55,0.10)" : "transparent" }}
              onMouseEnter={(e) => { if (selected !== viewer.id) e.currentTarget.style.background = C.bg3; }}
              onMouseLeave={(e) => { if (selected !== viewer.id) e.currentTarget.style.background = "transparent"; }}
            >
              <Avatar user={viewer} size={26} />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{viewer.name} <span style={{ color: C.text3 }}>(you)</span></div>
                <div className="text-xs truncate" style={{ color: C.text3 }}>{viewer.role}</div>
              </div>
            </button>
            {renderTree(viewer.id)}
          </div>
        </Card>
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {columns.map((col) => {
            const items = selTasks.filter((t) => col.match ? col.match(t.status) : t.status === col.key);
            return (
              <Card key={col.key} padding="p-0">
                <div className="px-3 py-2 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.border}` }}>
                  <div className="flex items-center gap-2 text-xs wcc-h">
                    <span className="rounded-sm" style={{ width: 8, height: 8, background: col.tone }} />
                    {col.label}
                  </div>
                  <Pill tone="neutral">{items.length}</Pill>
                </div>
                <div className="p-2 space-y-2 min-h-40">
                  {items.length === 0 ? (
                    <div className="text-xs text-center py-6" style={{ color: C.text3 }}>Nothing here.</div>
                  ) : (
                    items.map((t) => (
                      <div key={t.id} className="wcc-row cursor-pointer rounded-lg p-2.5" style={{ background: C.bg, border: `1px solid ${C.border}` }} onClick={() => onOpenTask(t.id)}>
                        <div className="text-xs" style={{ color: C.text }}>{t.title}</div>
                        <div className="flex items-center gap-1.5 mt-2">
                          <PriorityPill priority={t.priority} />
                          <span className="text-xs ml-auto" style={{ color: isOverdue(t) ? C.danger : C.text3 }}>{relTime(t.dueDate)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   REPORTS (Managers and above)
   ========================================================================= */
function ReportsScreen({ tasks, users }) {
  const [range, setRange] = useState("30d");
  const [dept, setDept] = useState("");

  const filtered = tasks.filter((t) => !dept || t.category === dept);

  const completionOverTime = Array.from({ length: 14 }).map((_, i) => ({
    day: new Date(NOW - (13 - i) * DAY).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
    rate: 55 + Math.round(Math.sin(i / 2) * 10 + i * 1.5),
  }));

  const overdueByDept = CATEGORIES.map((c) => ({
    name: c,
    overdue: filtered.filter((t) => t.category === c && isOverdue(t)).length,
  })).filter((d) => d.overdue > 0);

  const avgByPrio = PRIORITIES.map((p) => ({
    name: p.charAt(0).toUpperCase() + p.slice(1),
    days: p === "urgent" ? 1.2 : p === "high" ? 2.5 : p === "medium" ? 4.1 : 6.8,
  }));

  const byCategory = CATEGORIES.map((c, i) => {
    const palette = [C.gold, C.info, C.success, C.warning, C.danger, "#8B5CF6", "#EC4899", C.text3];
    return { name: c, value: filtered.filter((t) => t.category === c).length, fill: palette[i % palette.length] };
  }).filter((d) => d.value > 0);

  const bottlenecks = filtered.filter((t) => {
    if (t.status === "awaiting_help") return NOW - new Date(t.createdAt).getTime() > 2 * DAY;
    return isOverdue(t) && NOW - new Date(t.dueDate).getTime() > 7 * DAY;
  });

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="wcc-h text-xl">Reports</div>
          <div className="text-sm mt-0.5" style={{ color: C.text3 }}>Operational metrics across the group</div>
        </div>
        <div className="flex items-center gap-2">
          <select value={range} onChange={(e) => setRange(e.target.value)} className="wcc-input rounded-md px-3 py-1.5 text-sm">
            <option value="7d"  style={{ background: C.bg2 }}>Last 7 days</option>
            <option value="30d" style={{ background: C.bg2 }}>Last 30 days</option>
            <option value="90d" style={{ background: C.bg2 }}>Last 90 days</option>
          </select>
          <select value={dept} onChange={(e) => setDept(e.target.value)} className="wcc-input rounded-md px-3 py-1.5 text-sm">
            <option value="" style={{ background: C.bg2 }}>All departments</option>
            {CATEGORIES.map((c) => <option key={c} value={c} style={{ background: C.bg2 }}>{c}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card padding="p-0">
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}><div className="wcc-h text-sm">Completion rate over time</div></div>
          <div className="p-4" style={{ height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={completionOverTime}>
                <CartesianGrid stroke={C.border} vertical={false} />
                <XAxis dataKey="day" stroke={C.text3} tick={{ fill: C.text3, fontSize: 10 }} />
                <YAxis stroke={C.text3} tick={{ fill: C.text3, fontSize: 10 }} unit="%" />
                <Tooltip contentStyle={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }} />
                <Line type="monotone" dataKey="rate" stroke={C.gold} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card padding="p-0">
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}><div className="wcc-h text-sm">Overdue by department</div></div>
          <div className="p-4" style={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={overdueByDept}>
                <CartesianGrid stroke={C.border} vertical={false} />
                <XAxis dataKey="name" stroke={C.text3} tick={{ fill: C.text3, fontSize: 10 }} />
                <YAxis stroke={C.text3} tick={{ fill: C.text3, fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                <Bar dataKey="overdue" fill={C.danger} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card padding="p-0">
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}><div className="wcc-h text-sm">Avg. days to complete by priority</div></div>
          <div className="p-4" style={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={avgByPrio}>
                <CartesianGrid stroke={C.border} vertical={false} />
                <XAxis dataKey="name" stroke={C.text3} tick={{ fill: C.text3, fontSize: 10 }} />
                <YAxis stroke={C.text3} tick={{ fill: C.text3, fontSize: 10 }} unit="d" />
                <Tooltip contentStyle={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                <Bar dataKey="days" fill={C.info} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card padding="p-0">
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}><div className="wcc-h text-sm">Tasks by category</div></div>
          <div className="p-4 flex items-center gap-3" style={{ height: 240 }}>
            <div style={{ width: 160, height: 160 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={byCategory} dataKey="value" innerRadius={40} outerRadius={70} strokeWidth={0}>
                    {byCategory.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 grid grid-cols-1 gap-1">
              {byCategory.map((d) => (
                <div key={d.name} className="flex items-center gap-2 text-xs">
                  <span className="rounded-sm" style={{ width: 8, height: 8, background: d.fill }} />
                  <span className="flex-1" style={{ color: C.text2 }}>{d.name}</span>
                  <span className="wcc-num">{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      <Card padding="p-0">
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div className="wcc-h text-sm">Top bottlenecks</div>
            <div className="text-xs mt-0.5" style={{ color: C.text3 }}>Stuck &gt; 48h in help loop, or overdue &gt; 7 days</div>
          </div>
          <Pill tone="danger">{bottlenecks.length}</Pill>
        </div>
        {bottlenecks.length === 0 ? (
          <EmptyState icon={CheckCircle2} title="No bottlenecks" subtitle="Team is unblocked." />
        ) : (
          <div className="divide-y" style={{ borderColor: C.border }}>
            {bottlenecks.slice(0, 10).map((t) => (
              <div key={t.id} className="px-4 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{t.title}</div>
                  <div className="text-xs mt-0.5" style={{ color: C.text3 }}>{(userMap[t.assignedTo]?.name || "Unknown user")} · {t.category} · Due {absDate(t.dueDate)}</div>
                </div>
                <StatusPill status={t.status} />
                <PriorityPill priority={t.priority} />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* =========================================================================
   USERS & HIERARCHY (Admin/Director)
   ========================================================================= */
const ROLES = ["Intern", "Team Member", "Team Lead", "Manager", "Senior Manager", "Associate Director", "Admin", "Director", "CEO & Founder"];

function NewUserModal({ open, onClose, onCreate, users }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("Team Member");
  const [department, setDepartment] = useState("Sales");
  const [managerId, setManagerId] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName(""); setEmail(""); setPassword(""); setRole("Team Member");
    setDepartment("Sales"); setManagerId(""); setPhone(""); setError(""); setSaving(false);
  };

  const submit = async () => {
    if (!name.trim() || !email.trim() || !password.trim()) {
      setError("Name, email, and password are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onCreate({
        name: name.trim(),
        email: email.trim(),
        password,
        role,
        department,
        managerId: managerId || null,
        phone: phone.trim(),
      });
      reset();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Couldn't create user — check the details and try again.");
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="Add user"
      width={480}
      footer={
        <>
          <Button variant="secondary" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving ? "Creating…" : "Create user"}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Full name *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Meera Joshi" className="wcc-input w-full rounded-md px-3 py-2 text-sm" autoFocus />
        </div>
        <div>
          <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Work email *</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@wccsecurities.com" className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Temporary password *</label>
          <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="They can sign in with this right away" className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm">
              {ROLES.map((r) => <option key={r} value={r} style={{ background: C.bg2 }}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Department</label>
            <input value={department} onChange={(e) => setDepartment(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Reports to</label>
          <select value={managerId} onChange={(e) => setManagerId(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm">
            <option value="" style={{ background: C.bg2 }}>No manager (top of hierarchy)</option>
            {users.map((u) => <option key={u.id} value={u.id} style={{ background: C.bg2 }}>{u.name} — {u.role}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Phone</label>
          <div className="flex items-stretch gap-0 wcc-input rounded-md overflow-hidden" style={{ padding: 0 }}>
            <span className="flex items-center px-3 text-sm" style={{ color: C.text2, background: C.bg3, borderRight: `1px solid ${C.border}` }}>+91</span>
            <input
              value={phone.replace(/^\+91/, "")}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "").slice(-10);
                setPhone(digits ? `+91${digits}` : "");
              }}
              inputMode="numeric"
              maxLength={10}
              placeholder="98100 00000"
              className="flex-1 min-w-0 px-3 py-2 text-sm"
              style={{ background: "transparent", border: "none", outline: "none", color: C.text }}
            />
          </div>
        </div>
        {error && (
          <div className="text-xs px-3 py-2 rounded-md" style={{ background: "rgba(239,68,68,0.12)", color: C.danger, border: `1px solid rgba(239,68,68,0.3)` }}>
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

function UsersScreen({ users, viewer, onCreateUser, onUpdateUser, onDeleteUser, onSendReset, focusUserId, onFocusHandled }) {
  const [selected, setSelected] = useState(users[0].id);
  const [addUserOpen, setAddUserOpen] = useState(false);
  React.useEffect(() => {
    if (focusUserId) { setSelected(focusUserId); if (onFocusHandled) onFocusHandled(); }
  }, [focusUserId]); // eslint-disable-line react-hooks/exhaustive-deps
  const sel = users.find((u) => u.id === selected);
  const manager = sel?.managerId ? userMap[sel.managerId] : null;
  const reports = users.filter((u) => u.managerId === selected);

  const canEditUsers = canManageAll(viewer);

  // Normalizes any stored phone number into +91XXXXXXXXXX, whatever format
  // it happened to be saved in before the +91 prefix UI existed —
  // e.g. "9876543210", "919876543210", "+91 98765 43210" all become
  // "+919876543210". Returns "" if there's nothing usable.
  const normalizePhone = (raw) => {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, ""); // strip everything but digits
    if (!digits) return "";
    const last10 = digits.slice(-10); // the actual 10-digit number, ignoring any prefix
    return last10.length === 10 ? `+91${last10}` : "";
  };

  // Local editable copy of the selected user's fields.
  const [form, setForm] = useState({ name: "", email: "", role: "", department: "", managerId: "", phone: "", isActive: true });
  const [saving, setSaving] = useState(false);
  React.useEffect(() => {
    if (sel) setForm({ name: sel.name || "", email: sel.email || "", role: sel.role || "", department: sel.department || "", managerId: sel.managerId || "", phone: normalizePhone(sel.phone), isActive: sel.isActive !== false });
  }, [selected, sel]);

  const dirty = sel && (form.name !== (sel.name||"") || form.email !== (sel.email||"") || form.role !== sel.role || form.department !== (sel.department||"") || (form.managerId||"") !== (sel.managerId||"") || form.phone !== (sel.phone||"") || form.isActive !== (sel.isActive !== false));

  const saveEdits = async () => {
    if (!sel) return;
    setSaving(true);
    try {
      await onUpdateUser(sel.id, { name: form.name, email: form.email, role: form.role, department: form.department, managerId: form.managerId || null, phone: form.phone, isActive: form.isActive });
    } catch (err) {
      window.alert(err?.response?.data?.error || "Could not save changes.");
    } finally { setSaving(false); }
  };

  const removeUser = async () => {
    if (!sel) return;
    if (!window.confirm(`Delete ${sel.name} permanently? This cannot be undone.`)) return;
    try {
      await onDeleteUser(sel.id);
      const remaining = users.filter((u) => u.id !== sel.id);
      if (remaining.length) setSelected(remaining[0].id);
    } catch (err) {
      window.alert(err?.response?.data?.error || "Could not delete user.");
    }
  };

  const sendReset = async () => {
    if (!sel) return;
    try { await onSendReset(sel.id); }
    catch (err) { window.alert(err?.response?.data?.error || "Could not send reset email."); }
  };

  const roots = users.filter((u) => !u.managerId);
  const renderHierarchy = (rootId, depth = 0) => {
    const u = users.find((x) => x.id === rootId);
    if (!u) return null;
    const children = users.filter((x) => x.managerId === rootId);
    return (
      <div key={rootId} className="mb-1">
        <div
          className="rounded-lg px-3 py-2 flex items-center gap-2"
          style={{
            marginLeft: depth * 20,
            background: C.bg,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${depth === 0 ? C.gold : depth === 1 ? C.info : C.text3}`,
          }}
        >
          <Avatar user={u} size={24} />
          <div className="text-xs flex-1">
            <span className="wcc-h" style={{ color: C.text }}>{u.name}</span>
            <span style={{ color: C.text3 }}> · {u.role} · {u.department}</span>
          </div>
        </div>
        {children.length > 0 && <div className="mt-1">{children.map((c) => renderHierarchy(c.id, depth + 1))}</div>}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="wcc-h text-xl">Users & Hierarchy</div>
          <div className="text-sm mt-0.5" style={{ color: C.text3 }}>{users.length} members · {roots.length} at the top</div>
        </div>
        {canEditUsers && <Button icon={Plus} onClick={() => setAddUserOpen(true)}>Add user</Button>}
      </div>

      <NewUserModal open={addUserOpen} onClose={() => setAddUserOpen(false)} onCreate={onCreateUser} users={users} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card padding="p-0" className="lg:col-span-2">
          <div className="overflow-x-auto wcc-scroll">
            <table className="w-full" style={{ minWidth: 600 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: C.text3 }}>Name</th>
                  <th className="text-left px-3 py-3 text-xs font-medium" style={{ color: C.text3 }}>Role</th>
                  <th className="text-left px-3 py-3 text-xs font-medium" style={{ color: C.text3 }}>Manager</th>
                  <th className="text-left px-3 py-3 text-xs font-medium" style={{ color: C.text3 }}>Department</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const mgr = u.managerId ? userMap[u.managerId] : null;
                  const active = selected === u.id;
                  return (
                    <tr key={u.id} className="wcc-row cursor-pointer" style={{ borderBottom: `1px solid ${C.border}`, background: active ? "rgba(212,175,55,0.06)" : "transparent" }} onClick={() => setSelected(u.id)}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <Avatar user={u} size={26} />
                          <div>
                            <div className="text-sm">{u.name}</div>
                            <div className="text-xs" style={{ color: C.text3 }}>{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs" style={{ color: C.text2 }}>{u.role}</td>
                      <td className="px-3 py-2.5 text-xs" style={{ color: C.text2 }}>{mgr ? mgr.name : <span style={{ color: C.text3 }}>—</span>}</td>
                      <td className="px-3 py-2.5 text-xs" style={{ color: C.text2 }}>{u.department}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        <Card padding="p-4">
          <div className="text-xs mb-3" style={{ color: C.text3 }}>USER DETAILS</div>
          {sel && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <Avatar user={sel} size={48} />
                <div>
                  <div className="wcc-h text-sm">{sel.name}</div>
                  <div className="text-xs" style={{ color: C.text3 }}>{sel.role} · {sel.department}</div>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-xs mb-1" style={{ color: C.text3 }}>Name</div>
                  <input value={canEditUsers ? form.name : sel.name} onChange={(e) => setForm({ ...form, name: e.target.value })} readOnly={!canEditUsers} className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: C.text3 }}>Email</div>
                  <input
                    value={canEditUsers ? form.email : sel.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    readOnly={!canEditUsers}
                    className="wcc-input w-full rounded-md px-3 py-2 text-sm"
                    style={!canEditUsers ? { opacity: 0.7 } : undefined}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-xs mb-1" style={{ color: C.text3 }}>Role</div>
                    <select value={canEditUsers ? form.role : sel.role} onChange={(e) => setForm({ ...form, role: e.target.value })} disabled={!canEditUsers} className="wcc-input w-full rounded-md px-3 py-2 text-sm">
                      {ROLES.map((r) => <option key={r} value={r} style={{ background: C.bg2 }}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <div className="text-xs mb-1" style={{ color: C.text3 }}>Department</div>
                    <input value={canEditUsers ? form.department : sel.department} onChange={(e) => setForm({ ...form, department: e.target.value })} readOnly={!canEditUsers} className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
                  </div>
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: C.text3 }}>Reports to (manager)</div>
                  {canEditUsers ? (
                    <select value={form.managerId || ""} onChange={(e) => setForm({ ...form, managerId: e.target.value })} className="wcc-input w-full rounded-md px-3 py-2 text-sm">
                      <option value="" style={{ background: C.bg2 }}>— No manager (top of hierarchy)</option>
                      {users.filter((u) => u.id !== sel.id).map((u) => (
                        <option key={u.id} value={u.id} style={{ background: C.bg2 }}>{u.name} · {u.role}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="rounded-md px-3 py-2 text-sm flex items-center gap-2" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
                      {manager ? <><Avatar user={manager} size={20} /><span>{manager.name}</span></> : <span style={{ color: C.text3 }}>— No manager (top of hierarchy)</span>}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: C.text3 }}>Phone</div>
                  {canEditUsers ? (
                    <div className="flex items-stretch gap-0 wcc-input rounded-md overflow-hidden" style={{ padding: 0 }}>
                      <span className="flex items-center px-3 text-sm" style={{ color: C.text2, background: C.bg3, borderRight: `1px solid ${C.border}` }}>+91</span>
                      <input
                        value={(form.phone || "").replace(/^\+91/, "")}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, "").slice(-10);
                          setForm({ ...form, phone: digits ? `+91${digits}` : "" });
                        }}
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="98100 00000"
                        className="flex-1 min-w-0 px-3 py-2 text-sm"
                        style={{ background: "transparent", border: "none", outline: "none", color: C.text }}
                      />
                    </div>
                  ) : (
                    <input value={sel.phone} readOnly className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
                  )}
                </div>
                {canEditUsers && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: C.text2 }}>
                    <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                    Active (unchecking blocks sign-in without deleting)
                  </label>
                )}
                <div>
                  <div className="text-xs mb-1" style={{ color: C.text3 }}>Direct reports</div>
                  <div className="text-xs rounded-md px-3 py-2" style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text2 }}>
                    {reports.length === 0 ? <span style={{ color: C.text3 }}>None</span> : reports.map((r) => r.name).join(", ")}
                  </div>
                </div>

                {canEditUsers && (
                  <div className="pt-2 space-y-2" style={{ borderTop: `1px solid ${C.border}` }}>
                    <button onClick={saveEdits} disabled={!dirty || saving}
                      className="w-full rounded-md px-3 py-2 text-sm font-medium"
                      style={{ background: dirty && !saving ? C.gold : C.bg3, color: dirty && !saving ? "#0A1628" : C.text3, cursor: dirty && !saving ? "pointer" : "not-allowed" }}>
                      {saving ? "Saving…" : "Save changes"}
                    </button>
                    <button onClick={sendReset}
                      className="w-full rounded-md px-3 py-2 text-sm"
                      style={{ background: "transparent", border: `1px solid ${C.border}`, color: C.text2 }}>
                      Send password reset email
                    </button>
                    {sel.id !== viewer.id && (
                      <button onClick={removeUser}
                        className="w-full rounded-md px-3 py-2 text-sm"
                        style={{ background: "transparent", border: `1px solid #7F1D1D`, color: "#F87171" }}>
                        Delete user
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </Card>
      </div>

      <Card padding="p-4">
        <div className="wcc-h text-sm mb-3">Hierarchy tree</div>
        <div>{roots.map((r) => renderHierarchy(r.id))}</div>
      </Card>
    </div>
  );
}


/* =========================================================================
   PROJECTS
   ========================================================================= */
function ProjectsScreen({ projects, tasks, users, viewer, onCreateProject, onUpdateProject, onDeleteProject, onOpenTask, focusProjectId, onFocusHandled }) {
  const [selected, setSelected] = useState(projects[0]?.id || null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", description: "", ownerId: "", dueDate: "" });
  React.useEffect(() => {
    if (focusProjectId) { setSelected(focusProjectId); if (onFocusHandled) onFocusHandled(); }
  }, [focusProjectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [form, setForm] = useState({ name: "", description: "", ownerId: "", dueDate: "" });

  const canManage = canManageAll(viewer);
  const sel = projects.find((p) => p.id === selected) || null;
  const projTasks = tasks.filter((t) => t.projectId === selected && !t.parentTaskId);

  const statusColor = (s) => s === "completed" ? C.success : s === "on_hold" ? "#F59E0B" : s === "archived" ? C.text3 : C.info;

  const submitCreate = async () => {
    if (!form.name.trim()) { window.alert("Project name is required."); return; }
    try {
      const p = await onCreateProject({
        name: form.name.trim(),
        description: form.description,
        ownerId: form.ownerId || viewer.id,
        dueDate: form.dueDate || null,
      });
      setSelected(p.id);
      setCreateOpen(false);
      setForm({ name: "", description: "", ownerId: "", dueDate: "" });
    } catch (err) {
      window.alert(err?.response?.data?.error || "Could not create project.");
    }
  };

  const openEdit = () => {
    if (!sel) return;
    setEditForm({
      name: sel.name || "",
      description: sel.description || "",
      ownerId: sel.ownerId || "",
      dueDate: sel.dueDate ? new Date(sel.dueDate).toISOString().slice(0, 10) : "",
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!sel) return;
    if (!editForm.name.trim()) { window.alert("Project name is required."); return; }
    try {
      await onUpdateProject(sel.id, {
        name: editForm.name.trim(),
        description: editForm.description,
        ownerId: editForm.ownerId || viewer.id,
        dueDate: editForm.dueDate || null,
      });
      setEditOpen(false);
    } catch (err) {
      window.alert(err?.response?.data?.error || "Could not save changes.");
    }
  };

  const removeProject = async () => {
    if (!sel) return;
    if (!window.confirm(`Delete "${sel.name}"? Its tasks will be kept but detached from the project.`)) return;
    try {
      await onDeleteProject(sel.id);
      const remaining = projects.filter((p) => p.id !== sel.id);
      setSelected(remaining[0]?.id || null);
    } catch (err) {
      window.alert(err?.response?.data?.error || "Could not delete project.");
    }
  };

  const setProjectStatus = async (status) => {
    if (!sel) return;
    try { await onUpdateProject(sel.id, { status }); } catch (err) { window.alert("Could not update status."); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="wcc-h text-xl">Projects</div>
          <div className="text-sm mt-0.5" style={{ color: C.text3 }}>{projects.length} project{projects.length === 1 ? "" : "s"}</div>
        </div>
        <Button icon={Plus} onClick={() => setCreateOpen(true)}>New project</Button>
      </div>

      {projects.length === 0 ? (
        <EmptyState icon={Folder} title="No projects yet" subtitle="Create your first project to start grouping tasks." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: project list */}
          <div className="space-y-2">
            {projects.map((p) => {
              const count = tasks.filter((t) => t.projectId === p.id && !t.parentTaskId).length;
              const active = selected === p.id;
              return (
                <div key={p.id} onClick={() => setSelected(p.id)}
                  className="rounded-lg px-3 py-3 cursor-pointer"
                  style={{ background: active ? "rgba(212,175,55,0.08)" : C.bg2, border: `1px solid ${active ? C.gold : C.border}` }}>
                  <div className="flex items-center gap-2">
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: p.color || C.gold, display: "inline-block" }} />
                    <span className="wcc-h text-sm flex-1" style={{ color: C.text }}>{p.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: C.bg, color: statusColor(p.status) }}>{p.status}</span>
                  </div>
                  <div className="text-xs mt-1" style={{ color: C.text3 }}>{count} task{count === 1 ? "" : "s"}</div>
                </div>
              );
            })}
          </div>

          {/* Right: selected project detail */}
          <Card padding="p-4" className="lg:col-span-2">
            {sel ? (
              <>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="wcc-h text-lg">{sel.name}</div>
                    {sel.description && <div className="text-sm mt-1" style={{ color: C.text2 }}>{sel.description}</div>}
                    <div className="text-xs mt-1" style={{ color: C.text3 }}>
                      Owner: {userMap[sel.ownerId]?.name || "—"}
                      {sel.dueDate ? ` · Due ${new Date(sel.dueDate).toLocaleDateString()}` : ""}
                    </div>
                  </div>
                  {(canManage || sel.ownerId === viewer.id) && (
                    <div className="flex items-center gap-2">
                      <select value={sel.status} onChange={(e) => setProjectStatus(e.target.value)} className="wcc-input rounded-md px-2 py-1 text-xs">
                        {["active", "on_hold", "completed", "archived"].map((s) => <option key={s} value={s} style={{ background: C.bg2 }}>{s}</option>)}
                      </select>
                      <button onClick={openEdit} className="rounded-md px-2 py-1 text-xs" style={{ border: `1px solid ${C.border}`, color: C.text2, background: "transparent" }}>Edit</button>
                      <button onClick={removeProject} className="rounded-md px-2 py-1 text-xs" style={{ border: `1px solid #7F1D1D`, color: "#F87171", background: "transparent" }}>Delete</button>
                    </div>
                  )}
                </div>

                <div className="text-xs mb-2" style={{ color: C.text3 }}>TASKS IN THIS PROJECT</div>
                {projTasks.length === 0 ? (
                  <div className="text-sm rounded-md px-3 py-4 text-center" style={{ background: C.bg, border: `1px dashed ${C.border}`, color: C.text3 }}>
                    No tasks yet. Assign a task to this project from the task's edit screen.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {projTasks.map((t) => {
                      const subs = tasks.filter((x) => x.parentTaskId === t.id);
                      const done = subs.filter((x) => x.status === "completed" || x.status === "closed").length;
                      const taskAssignee = userMap[t.assignedTo];
                      const taskWatchers = (t.watcherIds || []).map((id) => userMap[id]).filter(Boolean);
                      return (
                        <div key={t.id} className="rounded-md overflow-hidden" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
                          {/* Parent task row */}
                          <div onClick={() => onOpenTask(t.id)} className="wcc-row cursor-pointer px-3 py-2.5 flex items-center gap-2 flex-wrap">
                            <CircleDot size={14} strokeWidth={1.5} style={{ color: C.text3, flexShrink: 0 }} />
                            <span className="text-sm flex-1 min-w-[120px]">{t.title}</span>
                            {taskAssignee && (
                              <div className="flex items-center gap-1.5" title={`Assigned to ${taskAssignee.name}`}>
                                <Avatar user={taskAssignee} size={20} />
                                <span className="text-xs" style={{ color: C.text3 }}>{taskAssignee.name}</span>
                              </div>
                            )}
                            {taskWatchers.length > 0 && (
                              <div className="flex items-center -space-x-1.5" title={`Tagged: ${taskWatchers.map((w) => w.name).join(", ")}`}>
                                {taskWatchers.slice(0, 4).map((w) => (
                                  <div key={w.id} style={{ border: `2px solid ${C.bg}`, borderRadius: "50%" }}><Avatar user={w} size={18} /></div>
                                ))}
                              </div>
                            )}
                            {subs.length > 0 && <span className="text-xs" style={{ color: C.text3 }}>{done}/{subs.length} subtasks</span>}
                            <PriorityPill priority={t.priority} />
                            <StatusPill status={t.status} />
                          </div>

                          {/* Sub-tasks, nested and indented under the parent */}
                          {subs.length > 0 && (
                            <div style={{ borderTop: `1px solid ${C.border}` }}>
                              {subs.map((st) => {
                                const stAssignee = userMap[st.assignedTo];
                                const stDone = st.status === "completed" || st.status === "closed";
                                return (
                                  <div key={st.id} onClick={() => onOpenTask(st.id)}
                                    className="wcc-row cursor-pointer pl-8 pr-3 py-2 flex items-center gap-2"
                                    style={{ background: C.bg2 }}>
                                    <CornerDownRight size={12} strokeWidth={1.5} style={{ color: C.text3, flexShrink: 0 }} />
                                    <span className="text-xs flex-1" style={{ color: stDone ? C.text3 : C.text2, textDecoration: stDone ? "line-through" : "none" }}>
                                      {st.title}
                                    </span>
                                    {stAssignee && <Avatar user={stAssignee} size={16} />}
                                    <StatusPill status={st.status} />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm" style={{ color: C.text3 }}>Select a project to see its details.</div>
            )}
          </Card>
        </div>
      )}

      {/* Create project modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="New project">
        <div className="space-y-3">
          <div>
            <div className="text-xs mb-1" style={{ color: C.text3 }}>Project name</div>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="wcc-input w-full rounded-md px-3 py-2 text-sm" placeholder="e.g. Q3 Client Onboarding" />
          </div>
          <div>
            <div className="text-xs mb-1" style={{ color: C.text3 }}>Description</div>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="wcc-input w-full rounded-md px-3 py-2 text-sm" rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-xs mb-1" style={{ color: C.text3 }}>Owner</div>
              <select value={form.ownerId} onChange={(e) => setForm({ ...form, ownerId: e.target.value })} className="wcc-input w-full rounded-md px-3 py-2 text-sm">
                <option value="" style={{ background: C.bg2 }}>{viewer.name} (you)</option>
                {users.filter((u) => u.id !== viewer.id).map((u) => <option key={u.id} value={u.id} style={{ background: C.bg2 }}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-xs mb-1" style={{ color: C.text3 }}>Due date (optional)</div>
              <input type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={submitCreate}>Create project</Button>
          </div>
        </div>
      </Modal>

      {/* Edit project modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Edit project">
        <div className="space-y-3">
          <div>
            <div className="text-xs mb-1" style={{ color: C.text3 }}>Project name</div>
            <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="wcc-input w-full rounded-md px-3 py-2 text-sm" placeholder="e.g. Q3 Client Onboarding" />
          </div>
          <div>
            <div className="text-xs mb-1" style={{ color: C.text3 }}>Description</div>
            <textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="wcc-input w-full rounded-md px-3 py-2 text-sm" rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-xs mb-1" style={{ color: C.text3 }}>Owner</div>
              <select value={editForm.ownerId} onChange={(e) => setEditForm({ ...editForm, ownerId: e.target.value })} className="wcc-input w-full rounded-md px-3 py-2 text-sm">
                <option value={viewer.id} style={{ background: C.bg2 }}>{viewer.name} (you)</option>
                {users.filter((u) => u.id !== viewer.id).map((u) => <option key={u.id} value={u.id} style={{ background: C.bg2 }}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-xs mb-1" style={{ color: C.text3 }}>Due date (optional)</div>
              <input type="date" value={editForm.dueDate} onChange={(e) => setEditForm({ ...editForm, dueDate: e.target.value })} className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={submitEdit}>Save changes</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* =========================================================================
   SETTINGS
   ========================================================================= */
function SettingsScreen({ viewer, onSignOut, onUploadAvatar, onRemoveAvatar }) {
  const eventTypes = [
    "Task assigned to me",
    "Comment or @mention on my task",
    "Help requested from me",
    "Status changes on tasks I created",
    "Overdue reminders",
    "Reminders (scheduled fires)",
  ];
  const [prefs, setPrefs] = useState(eventTypes.reduce((acc, e) => { acc[e] = { inapp: true, email: e !== "Reminders (scheduled fires)", whatsapp: e === "Task assigned to me" || e === "Help requested from me" || e === "Overdue reminders" }; return acc; }, {}));
  const toggle = (event, ch) => setPrefs({ ...prefs, [event]: { ...prefs[event], [ch]: !prefs[event][ch] } });

  const [officeFrom, setOfficeFrom] = useState("09:30");
  const [officeTo, setOfficeTo] = useState("18:30");
  const [workDays, setWorkDays] = useState(["Mon","Tue","Wed","Thu","Fri","Sat"]);
  const toggleDay = (d) => setWorkDays(workDays.includes(d) ? workDays.filter((x) => x !== d) : [...workDays, d]);

  const [avatarBusy, setAvatarBusy] = useState(false);
  const handleAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow picking the same file again later
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      window.alert("Image is too large (max 5MB).");
      return;
    }
    setAvatarBusy(true);
    try {
      await onUploadAvatar(viewer.id, file);
    } catch (err) {
      window.alert(err?.response?.data?.error || "Could not upload profile picture.");
    } finally {
      setAvatarBusy(false);
    }
  };
  const handleAvatarRemove = async () => {
    setAvatarBusy(true);
    try {
      await onRemoveAvatar(viewer.id);
    } catch (err) {
      window.alert(err?.response?.data?.error || "Could not remove profile picture.");
    } finally {
      setAvatarBusy(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-3xl">
      <div>
        <div className="wcc-h text-xl">Settings</div>
        <div className="text-sm mt-0.5" style={{ color: C.text3 }}>Your profile, notifications, and working hours</div>
      </div>

      <Card>
        <div className="wcc-h text-sm mb-4">My profile</div>
        <div className="flex items-center gap-4 mb-4">
          <Avatar user={viewer} size={56} />
          <div className="flex-1">
            <div className="wcc-h text-base">{viewer.name}</div>
            <div className="text-xs" style={{ color: C.text3 }}>{viewer.role} · {viewer.department}</div>
          </div>
          <div className="flex items-center gap-2">
            <label className="wcc-btn text-xs rounded-md px-3 py-1.5 cursor-pointer" style={{ border: `1px solid ${C.border}`, color: C.text2, opacity: avatarBusy ? 0.6 : 1 }}>
              {avatarBusy ? "Working…" : "Change picture"}
              <input type="file" accept="image/*" onChange={handleAvatarPick} disabled={avatarBusy} style={{ display: "none" }} />
            </label>
            {viewer.avatarUrl && (
              <button onClick={handleAvatarRemove} disabled={avatarBusy} className="wcc-btn text-xs rounded-md px-3 py-1.5" style={{ border: `1px solid ${C.border}`, color: "#F87171" }}>
                Remove
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Name</label>
            <input value={viewer.name} readOnly className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Email</label>
            <input value={viewer.email} readOnly className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>WhatsApp number</label>
            <input value={viewer.phone} readOnly className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Avatar initials</label>
            <input value={viewer.avatarInitials} readOnly className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
          </div>
        </div>
      </Card>

      <Card padding="p-0">
        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="wcc-h text-sm">Notification preferences</div>
          <div className="text-xs mt-0.5" style={{ color: C.text3 }}>Choose how each event reaches you.</div>
        </div>
        <div className="p-4">
          <div className="grid gap-2" style={{ gridTemplateColumns: "1fr 90px 90px 90px" }}>
            <div className="text-xs" style={{ color: C.text3 }}>Event</div>
            <div className="text-xs text-center flex items-center justify-center gap-1" style={{ color: C.text3 }}><Bell size={12} strokeWidth={1.5} /> In-app</div>
            <div className="text-xs text-center flex items-center justify-center gap-1" style={{ color: C.text3 }}><Mail size={12} strokeWidth={1.5} /> Email</div>
            <div className="text-xs text-center flex items-center justify-center gap-1" style={{ color: C.text3 }}><MessageCircle size={12} strokeWidth={1.5} /> WhatsApp</div>
            {eventTypes.map((e) => (
              <React.Fragment key={e}>
                <div className="text-sm py-2" style={{ color: C.text }}>{e}</div>
                {["inapp","email","whatsapp"].map((ch) => (
                  <div key={ch} className="flex items-center justify-center py-2">
                    <button
                      onClick={() => toggle(e, ch)}
                      className="wcc-btn rounded-full relative"
                      style={{
                        width: 32, height: 18,
                        background: prefs[e][ch] ? C.gold : C.bg3,
                        border: `1px solid ${prefs[e][ch] ? C.gold : C.border}`,
                      }}
                      aria-pressed={prefs[e][ch]}
                    >
                      <span
                        className="rounded-full absolute top-0.5"
                        style={{
                          width: 12, height: 12,
                          background: prefs[e][ch] ? C.bg : C.text3,
                          left: prefs[e][ch] ? 16 : 2,
                          transition: "left 200ms cubic-bezier(0.4,0,0.2,1)",
                        }}
                      />
                    </button>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        <div className="wcc-h text-sm mb-3">Office hours</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Start</label>
            <input type="time" value={officeFrom} onChange={(e) => setOfficeFrom(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>End</label>
            <input type="time" value={officeTo} onChange={(e) => setOfficeTo(e.target.value)} className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Timezone</label>
            <input value="Asia/Kolkata · IST" readOnly className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Working days</label>
          <div className="flex flex-wrap gap-1.5">
            {DAYS_SHORT.map((d) => {
              const on = workDays.includes(d);
              return (
                <button key={d} onClick={() => toggleDay(d)} className="wcc-btn rounded-full px-3 py-1 text-xs"
                  style={{ background: on ? "rgba(212,175,55,0.15)" : C.bg, color: on ? C.gold : C.text2, border: `1px solid ${on ? "rgba(212,175,55,0.4)" : C.border}` }}>
                  {d}
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      <Card>
        <div className="wcc-h text-sm mb-3">Account</div>
        <Button variant="danger" icon={LogOut} onClick={onSignOut}>Sign out</Button>
      </Card>
    </div>
  );
}

/* =========================================================================
   COMMAND PALETTE (⌘K)
   ========================================================================= */
function CommandPalette({ open, onClose, tasks, users, projects = [], onOpenTask, onOpenUser, onOpenProject, onNavigate }) {
  const [q, setQ] = useState("");
  useEffect(() => { if (open) setQ(""); }, [open]);
  if (!open) return null;

  const query = q.trim().toLowerCase();
  const taskHits = query ? tasks.filter((t) => t.title.toLowerCase().includes(query) || t.tags.join(",").toLowerCase().includes(query)).slice(0, 6) : tasks.slice(0, 5);
  const userHits = query ? users.filter((u) => u.name.toLowerCase().includes(query) || u.role.toLowerCase().includes(query)).slice(0, 4) : [];
  const navHits = query ? NAV_ITEMS.filter((n) => n.label.toLowerCase().includes(query)).slice(0, 3) : [];
  const projectHits = query ? projects.filter((p) => p.name.toLowerCase().includes(query)).slice(0, 4) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-20" style={{ background: "rgba(4,10,20,0.72)" }} onClick={onClose}>
      <div
        className="wcc-scale-in rounded-xl overflow-hidden"
        style={{
          background: C.bg2,
          border: `1px solid ${C.border}`,
          boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
          width: "100%",
          maxWidth: 560,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
          <Search size={16} strokeWidth={1.5} style={{ color: C.text3 }} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tasks, people, or jump to a page…"
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: C.text }}
          />
          <kbd className="text-xs px-1.5 py-0.5 rounded" style={{ background: C.bg3, color: C.text2, border: `1px solid ${C.border}` }}>Esc</kbd>
        </div>
        <div className="max-h-96 overflow-y-auto wcc-scroll">
          {taskHits.length > 0 && (
            <div className="px-2 py-2">
              <div className="text-xs px-2 py-1" style={{ color: C.text3 }}>Tasks</div>
              {taskHits.map((t) => (
                <button key={t.id} className="wcc-btn w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left" onClick={() => { onOpenTask(t.id); onClose(); }}
                  onMouseEnter={(e) => e.currentTarget.style.background = C.bg3}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                  <div className="rounded-md p-1.5" style={{ background: C.bg3 }}>
                    <ListChecks size={14} strokeWidth={1.5} style={{ color: C.gold }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{t.title}</div>
                    <div className="text-xs" style={{ color: C.text3 }}>{(userMap[t.assignedTo]?.name || "Unknown user")} · {t.category}</div>
                  </div>
                  <StatusPill status={t.status} />
                </button>
              ))}
            </div>
          )}
          {userHits.length > 0 && (
            <div className="px-2 py-2" style={{ borderTop: `1px solid ${C.border}` }}>
              <div className="text-xs px-2 py-1" style={{ color: C.text3 }}>People</div>
              {userHits.map((u) => (
                <button key={u.id} className="wcc-btn w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left" onClick={() => { if (onOpenUser) onOpenUser(u.id); onClose(); }}
                  onMouseEnter={(e) => e.currentTarget.style.background = C.bg3}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                  <Avatar user={u} size={26} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{u.name}</div>
                    <div className="text-xs" style={{ color: C.text3 }}>{u.role} · {u.department}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {projectHits.length > 0 && (
            <div className="px-2 py-2" style={{ borderTop: `1px solid ${C.border}` }}>
              <div className="text-xs px-2 py-1" style={{ color: C.text3 }}>Projects</div>
              {projectHits.map((p) => (
                <button key={p.id} className="wcc-btn w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left" onClick={() => { if (onOpenProject) onOpenProject(p.id); onClose(); }}
                  onMouseEnter={(e) => e.currentTarget.style.background = C.bg3}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                  <div className="rounded-md p-1.5" style={{ background: C.bg3 }}>
                    <Folder size={14} strokeWidth={1.5} style={{ color: C.gold }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{p.name}</div>
                    <div className="text-xs" style={{ color: C.text3 }}>{p.status}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {navHits.length > 0 && (
            <div className="px-2 py-2" style={{ borderTop: `1px solid ${C.border}` }}>
              <div className="text-xs px-2 py-1" style={{ color: C.text3 }}>Pages</div>
              {navHits.map((n) => (
                <button key={n.key} className="wcc-btn w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left" onClick={() => { onNavigate(n.key); onClose(); }}
                  onMouseEnter={(e) => e.currentTarget.style.background = C.bg3}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                  <div className="rounded-md p-1.5" style={{ background: C.bg3 }}>
                    <n.icon size={14} strokeWidth={1.5} style={{ color: C.gold }} />
                  </div>
                  <div className="text-sm">{n.label}</div>
                </button>
              ))}
            </div>
          )}
          {query && taskHits.length + userHits.length + projectHits.length + navHits.length === 0 && (
            <div className="p-8 text-center text-sm" style={{ color: C.text3 }}>No results for "{query}"</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   NOTIFICATION SIMULATOR (KEY DEMO FEATURE)
   ========================================================================= */

/* =========================================================================
   TOASTS
   ========================================================================= */
function ToastContainer({ toasts, onDismiss }) {
  return (
    <div className="fixed z-50 flex flex-col gap-2" style={{ top: 72, right: 16, maxWidth: 340 }}>
      {toasts.map((t) => (
        <div
          key={t.id}
          className="wcc-toast rounded-xl p-3 flex items-start gap-3"
          style={{ background: C.bg2, border: `1px solid ${C.border}`, boxShadow: "0 4px 24px rgba(0,0,0,0.35)" }}
        >
          <div className="rounded-lg p-1.5 flex-shrink-0" style={{ background: "rgba(212,175,55,0.15)" }}>
            <Bell size={14} strokeWidth={1.5} style={{ color: C.gold }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs wcc-h mb-0.5">{t.title}</div>
            <div className="text-xs" style={{ color: C.text2 }}>{t.message}</div>
          </div>
          <IconButton icon={X} label="Dismiss" onClick={() => onDismiss(t.id)} />
        </div>
      ))}
    </div>
  );
}

/* =========================================================================
   USER PICKER MODAL (for reassign / ask help)
   ========================================================================= */
function UserPickerModal({ open, onClose, onPick, title, subtitle, users, excludeIds = [], initialNote = false }) {
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => { if (open) { setQ(""); setPick(null); setNote(""); } }, [open]);
  const filtered = users.filter((u) => !excludeIds.includes(u.id) && (q === "" || u.name.toLowerCase().includes(q.toLowerCase()) || u.role.toLowerCase().includes(q.toLowerCase())));
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      width={480}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!pick} onClick={() => onPick(pick, note)}>Confirm</Button>
        </>
      }
    >
      {subtitle && <div className="text-sm mb-3" style={{ color: C.text2 }}>{subtitle}</div>}
      <TextInput icon={Search} placeholder="Search by name or role…" value={q} onChange={(e) => setQ(e.target.value)} className="mb-3" />
      <div className="max-h-72 overflow-y-auto wcc-scroll rounded-lg" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-xs" style={{ color: C.text3 }}>No matching users.</div>
        ) : (
          filtered.map((u) => (
            <button key={u.id} onClick={() => setPick(u.id)} className="wcc-btn w-full flex items-center gap-3 px-3 py-2 text-left"
              style={{ background: pick === u.id ? "rgba(212,175,55,0.10)" : "transparent", borderBottom: `1px solid ${C.border}` }}
              onMouseEnter={(e) => { if (pick !== u.id) e.currentTarget.style.background = C.bg3; }}
              onMouseLeave={(e) => { if (pick !== u.id) e.currentTarget.style.background = "transparent"; }}>
              <Avatar user={u} size={30} />
              <div className="flex-1 min-w-0">
                <div className="text-sm">{u.name}</div>
                <div className="text-xs" style={{ color: C.text3 }}>{u.role} · {u.department}</div>
              </div>
              {pick === u.id && <Check size={16} strokeWidth={2} style={{ color: C.gold }} />}
            </button>
          ))
        )}
      </div>
      {initialNote && (
        <div className="mt-3">
          <label className="text-xs mb-1.5 block" style={{ color: C.text3 }}>Note to helper (optional)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Briefly describe what you need…" className="wcc-input w-full rounded-md px-3 py-2 text-sm" />
        </div>
      )}
    </Modal>
  );
}

/* =========================================================================
   REDUCER — task state
   ========================================================================= */
const initialState = {
  tasks: [],
  reminders: [],
  helpRequests: [],
  comments: [],
  activity: [],
  notifications: [],
  users: [],
  projects: [],
  loaded: false,
};

let idCounter = 1000;
const nid = () => (++idCounter).toString();

function appReducer(state, action) {
  switch (action.type) {
    case "HYDRATE": {
      return {
        ...state,
        users: action.users,
        tasks: action.tasks,
        reminders: action.reminders,
        helpRequests: action.helpRequests,
        comments: action.comments,
        activity: action.activity,
        notifications: action.notifications,
        projects: action.projects || [],
        loaded: true,
      };
    }
    case "ADD_PROJECT": {
      return { ...state, projects: [action.project, ...state.projects] };
    }
    case "UPDATE_PROJECT": {
      return { ...state, projects: state.projects.map((p) => p.id === action.project.id ? action.project : p) };
    }
    case "DELETE_PROJECT": {
      // Project removed; its tasks are detached (projectId cleared) to match the backend.
      return {
        ...state,
        projects: state.projects.filter((p) => p.id !== action.projectId),
        tasks: state.tasks.map((t) => t.projectId === action.projectId ? { ...t, projectId: null } : t),
      };
    }
    case "ADD_USER": {
      return { ...state, users: [...state.users, action.user] };
    }
    case "UPDATE_USER": {
      return { ...state, users: state.users.map((u) => u.id === action.user.id ? action.user : u) };
    }
    case "DELETE_USER": {
      return { ...state, users: state.users.filter((u) => u.id !== action.userId) };
    }
    case "SET_STATUS": {
      return {
        ...state,
        tasks: state.tasks.map((t) => t.id === action.taskId ? { ...t, status: action.status, closedAt: action.status === "closed" ? iso(NOW) : t.closedAt } : t),
        activity: [
          { id: `a${nid()}`, taskId: action.taskId, userId: action.userId, action: action.status === "closed" ? "closed" : "status_changed", details: action.details || `Status → ${action.status.replace(/_/g," ")}`, timestamp: iso(NOW) },
          ...state.activity,
        ],
      };
    }
    case "REASSIGN": {
      const t = state.tasks.find((x) => x.id === action.taskId);
      const from = userMap[t.assignedTo];
      const to = userMap[action.newAssignee];
      return {
        ...state,
        tasks: state.tasks.map((x) => x.id === action.taskId ? { ...x, assignedTo: action.newAssignee, status: "assigned", currentHelperId: null } : x),
        activity: [
          { id: `a${nid()}`, taskId: action.taskId, userId: action.userId, action: "reassigned", details: `Reassigned from ${from.name} to ${to.name}`, timestamp: iso(NOW) },
          ...state.activity,
        ],
      };
    }
    case "ASK_HELP": {
      return {
        ...state,
        tasks: state.tasks.map((t) => t.id === action.taskId ? { ...t, status: "awaiting_help", currentHelperId: action.helperId } : t),
        helpRequests: [
          { id: `h${nid()}`, taskId: action.taskId, requestedBy: action.userId, helperUserId: action.helperId, note: action.note || "Needs support to close this out.", status: "pending", createdAt: iso(NOW), resolvedAt: null },
          ...state.helpRequests,
        ],
        activity: [
          { id: `a${nid()}`, taskId: action.taskId, userId: action.userId, action: "help_requested", details: `Asked ${(userMap[action.helperId]?.name || "Unknown user")} for help`, timestamp: iso(NOW) },
          ...state.activity,
        ],
      };
    }
    case "ACCEPT_HELP": {
      const t = state.tasks.find((x) => x.id === action.taskId);
      return {
        ...state,
        tasks: state.tasks.map((x) => x.id === action.taskId ? { ...x, status: "help_in_progress" } : x),
        helpRequests: state.helpRequests.map((h) => h.taskId === action.taskId && h.status === "pending" ? { ...h, status: "accepted" } : h),
        activity: [
          { id: `a${nid()}`, taskId: action.taskId, userId: action.userId, action: "help_accepted", details: `${(userMap[action.userId]?.name || "Unknown user")} accepted the help request`, timestamp: iso(NOW) },
          ...state.activity,
        ],
      };
    }
    case "DECLINE_HELP": {
      return {
        ...state,
        tasks: state.tasks.map((x) => x.id === action.taskId ? { ...x, status: "in_progress", currentHelperId: null } : x),
        helpRequests: state.helpRequests.map((h) => h.taskId === action.taskId && h.status === "pending" ? { ...h, status: "declined", resolvedAt: iso(NOW) } : h),
        activity: [
          { id: `a${nid()}`, taskId: action.taskId, userId: action.userId, action: "status_changed", details: `Help declined by ${(userMap[action.userId]?.name || "Unknown user")}`, timestamp: iso(NOW) },
          ...state.activity,
        ],
      };
    }
    case "COMPLETE_HELP": {
      return {
        ...state,
        tasks: state.tasks.map((x) => x.id === action.taskId ? { ...x, status: "in_progress", currentHelperId: null } : x),
        helpRequests: state.helpRequests.map((h) => h.taskId === action.taskId && h.status === "accepted" ? { ...h, status: "completed", resolvedAt: iso(NOW) } : h),
        activity: [
          { id: `a${nid()}`, taskId: action.taskId, userId: action.userId, action: "help_completed", details: `${(userMap[action.userId]?.name || "Unknown user")} completed their help — back to assignee`, timestamp: iso(NOW) },
          ...state.activity,
        ],
      };
    }
    case "ADD_COMMENT": {
      const comment = action.serverComment || { id: `c${nid()}`, taskId: action.taskId, userId: action.userId, text: action.text, mentions: [], createdAt: iso(NOW) };
      return {
        ...state,
        comments: [...state.comments, comment],
        activity: [
          { id: `a${nid()}`, taskId: action.taskId, userId: action.userId, action: "commented", details: "Added a comment", timestamp: iso(NOW) },
          ...state.activity,
        ],
      };
    }
    case "ADD_REMINDER": {
      const rem = action.serverReminder || { id: `r${nid()}`, taskId: action.taskId, type: action.reminder.type, config: action.reminder.config, active: true, nextFireAt: iso(NOW + HOUR) };
      return {
        ...state,
        reminders: [...state.reminders, rem],
        tasks: state.tasks.map((t) => t.id === action.taskId ? { ...t, reminderIds: [...t.reminderIds, rem.id] } : t),
        activity: [
          { id: `a${nid()}`, taskId: action.taskId, userId: action.userId, action: "reminder_added", details: `Added reminder: ${reminderLabel(rem)}`, timestamp: iso(NOW) },
          ...state.activity,
        ],
      };
    }
    case "DELETE_REMINDER": {
      return {
        ...state,
        reminders: state.reminders.filter((r) => r.id !== action.reminderId),
        tasks: state.tasks.map((t) => ({ ...t, reminderIds: t.reminderIds.filter((id) => id !== action.reminderId) })),
      };
    }
    case "EDIT_TITLE": {
      return {
        ...state,
        tasks: state.tasks.map((t) => t.id === action.taskId ? { ...t, title: action.title } : t),
      };
    }
    case "EDIT_TASK": {
      return {
        ...state,
        tasks: state.tasks.map((t) => t.id === action.taskId ? { ...t, ...action.updates } : t),
        activity: [
          { id: `a${nid()}`, taskId: action.taskId, userId: action.userId, action: "edited", details: `${(userMap[action.userId]?.name || "Unknown user")} updated the task details`, timestamp: iso(NOW) },
          ...state.activity,
        ],
      };
    }
    case "CREATE_TASK": {
      if (action.serverTask) {
        return {
          ...state,
          tasks: [action.serverTask, ...state.tasks],
          reminders: [...state.reminders, ...(action.serverReminders || [])],
          activity: [...(action.serverActivity || []), ...state.activity],
        };
      }
      const tid = `t${nid()}`;
      const newT = {
        id: tid,
        title: action.data.title,
        description: action.data.description || "",
        createdBy: action.userId,
        assignedTo: action.data.assignedTo,
        currentHelperId: null,
        status: action.data.status || "assigned",
        priority: action.data.priority,
        dueDate: action.data.dueDate,
        category: action.data.category,
        tags: action.data.tags || [],
        createdAt: iso(NOW),
        closedAt: null,
        reminderIds: [],
      };
      const newReminders = (action.data.reminders || []).map((r) => ({ id: `r${nid()}`, taskId: tid, type: r.type, config: r.config, active: true, nextFireAt: iso(NOW + HOUR) }));
      newT.reminderIds = newReminders.map((r) => r.id);
      const newActivity = [
        { id: `a${nid()}`, taskId: tid, userId: action.userId, action: "created", details: "Task created", timestamp: iso(NOW) },
      ];
      if (newT.status !== "draft") {
        newActivity.push({ id: `a${nid()}`, taskId: tid, userId: action.userId, action: "assigned", details: `Assigned to ${(userMap[newT.assignedTo]?.name || "Unknown user")}`, timestamp: iso(NOW) });
      }
      return {
        ...state,
        tasks: [newT, ...state.tasks],
        reminders: [...state.reminders, ...newReminders],
        activity: [...newActivity, ...state.activity],
      };
    }
    case "DELETE_TASK": {
      // Remove the task and anything attached to it from local state.
      return {
        ...state,
        tasks: state.tasks.filter((t) => t.id !== action.taskId),
        comments: state.comments.filter((c) => c.taskId !== action.taskId),
        activity: state.activity.filter((a) => a.taskId !== action.taskId),
        reminders: state.reminders.filter((r) => r.taskId !== action.taskId),
        notifications: state.notifications.filter((n) => n.taskId !== action.taskId),
      };
    }
    case "DUPLICATE": {
      if (action.serverTask) return { ...state, tasks: [action.serverTask, ...state.tasks] };
      const src = state.tasks.find((t) => t.id === action.taskId);
      if (!src) return state;
      const tid = `t${nid()}`;
      const newT = { ...src, id: tid, title: `${src.title} (copy)`, status: "draft", createdAt: iso(NOW), reminderIds: [] };
      return { ...state, tasks: [newT, ...state.tasks] };
    }
    case "MARK_ALL_NOTIFS_READ": {
      return { ...state, notifications: state.notifications.map((n) => n.userId === action.userId ? { ...n, read: true } : n) };
    }
    case "ADD_NOTIFICATION": {
      return { ...state, notifications: [{ id: `n${nid()}`, ...action.notification, createdAt: iso(NOW), read: false }, ...state.notifications] };
    }
    case "RECEIVE_NOTIFICATION": {
      // A real-time push from the server — it already has a real id/createdAt.
      // Ignore it if we've somehow already got it (e.g. the polling fallback
      // picked it up first).
      if (state.notifications.some((n) => n.id === action.notification.id)) return state;
      return { ...state, notifications: [action.notification, ...state.notifications] };
    }
    case "SET_NOTIFICATIONS": {
      // Wholesale sync from the polling fallback — always matches server truth.
      return { ...state, notifications: action.notifications };
    }
    default:
      return state;
  }
}

/* =========================================================================
   MAIN APP
   ========================================================================= */
export default function WCCTaskFlow() {
  const [authToken, setAuthTokenState] = useState(() => localStorage.getItem("wcc_token"));
  const [currentUserId, setCurrentUserId] = useState(null);
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  // "login" | "forgot" | "reset" — reset is entered automatically when the URL carries ?reset_token=
  const [authScreen, setAuthScreen] = useState("login");
  const [resetToken, setResetToken] = useState(() => new URLSearchParams(window.location.search).get("reset_token"));
  const [screen, setScreenRaw] = useState(() => {
    try { return localStorage.getItem("wcc_taskflow_screen") || "dashboard"; }
    catch { return "dashboard"; }
  });
  // Wrap setScreen so every navigation also remembers where we are,
  // so a page refresh reopens the same tab instead of resetting to Dashboard.
  const setScreen = (next) => {
    setScreenRaw(next);
    try { localStorage.setItem("wcc_taskflow_screen", next); } catch {}
  };
  const [state, dispatch] = useReducer(appReducer, initialState);
  const [taskDetailId, setTaskDetailId] = useState(null);
  const [focusUserId, setFocusUserId] = useState(null);
  const [profileCardUserId, setProfileCardUserId] = useState(null);
  const [focusProjectId, setFocusProjectId] = useState(null);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [notifsOpen, setNotifsOpen] = useState(false);

  // Open a task directly from a link (email/WhatsApp notifications include
  // ?task=<id>), once the app has actually finished loading data. Placed
  // here — before any early return below — so this hook always runs in the
  // same order every render, and references the setters directly (rather
  // than the openTask helper, which is only defined further down, after
  // those early-return checks).
  useEffect(() => {
    if (!state.loaded) return;
    const params = new URLSearchParams(window.location.search);
    const taskId = params.get("task");
    if (taskId) {
      setTaskDetailId(taskId);
      setNotifsOpen(false);
      // Clean the URL so a later refresh doesn't keep reopening this task.
      params.delete("task");
      const newSearch = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (newSearch ? `?${newSearch}` : ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loaded]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [pickerModal, setPickerModal] = useState(null); // { mode: "reassign"|"ask_help", taskId }
  const [toasts, setToasts] = useState([]);

  const users = state.users;
  const viewer = currentUserId ? users.find((u) => u.id === currentUserId) : null;

  const pushToast = (t) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    setToasts((tt) => [...tt, { id, ...t }]);
    setTimeout(() => setToasts((tt) => tt.filter((x) => x.id !== id)), 4000);
  };

  const dismissToast = (id) => setToasts((tt) => tt.filter((x) => x.id !== id));

  // Load the current session (if any) + hydrate all app data from the API on first mount
  const loadBootstrap = async () => {
    const { data } = await api.get("/bootstrap");
    Object.keys(userMap).forEach((k) => delete userMap[k]);
    Object.assign(userMap, byId(data.users));
    dispatch({
      type: "HYDRATE",
      users: data.users, tasks: data.tasks, reminders: data.reminders,
      helpRequests: data.helpRequests, comments: data.comments,
      activity: data.activity, notifications: data.notifications,
      projects: data.projects || [],
    });
    setCurrentUserId(data.currentUser.id);
  };

  // Real-time notifications: connect via Socket.IO while logged in, with a
  // slow polling fallback underneath. If the socket never connects (blocked
  // by a proxy, hosting limitation, etc.) the poll still keeps notifications
  // reasonably fresh — nothing is silently lost, it just arrives on the next
  // poll instead of instantly.
  useEffect(() => {
    if (!authToken || !currentUserId) return;

    const socketUrl = (process.env.REACT_APP_API_URL || "http://localhost:5000/api").replace(/\/api\/?$/, "");
    const socket = io(socketUrl, {
      auth: { token: authToken },
      reconnectionDelay: 3000,
      timeout: 8000,
    });

    socket.on("notification", (notification) => {
      dispatch({ type: "RECEIVE_NOTIFICATION", notification });
      pushToast({ title: "New notification", message: notification.message });
    });

    // Safety-net poll — independent of whether the socket is connected.
    const pollId = setInterval(() => {
      api.get("/notifications")
        .then(({ data }) => dispatch({ type: "SET_NOTIFICATIONS", notifications: data }))
        .catch(() => {}); // a missed poll isn't worth surfacing an error for
    }, 45000);

    return () => {
      socket.disconnect();
      clearInterval(pollId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, currentUserId]);

  useEffect(() => {
    // A reset link takes priority over any existing session — don't auto-hydrate in that case.
    if (!authToken || resetToken) return;
    setAuthToken(authToken);
    loadBootstrap().catch(() => {
      // token expired/invalid — drop back to the login screen
      localStorage.removeItem("wcc_token");
      setAuthTokenState(null);
      setAuthToken(null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (viewer) setCmdOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewer]);

  const handleSignIn = async (email, password) => {
    setAuthError("");
    setAuthLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("wcc_token", data.token);
      setAuthToken(data.token);
      setAuthTokenState(data.token);
      setScreen("dashboard");
    } catch (err) {
      setAuthError(err?.response?.data?.error || "Sign-in failed. Check your email and password.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem("wcc_token");
    localStorage.removeItem("wcc_taskflow_screen");
    setAuthToken(null);
    setAuthTokenState(null);
    setCurrentUserId(null);
  };

  // Throws on failure so ForgotPasswordScreen/ResetPasswordScreen can show the error inline.
  const handleRequestReset = async (email) => {
    await api.post("/auth/forgot-password", { email });
  };

  const handleResetPassword = async (token, newPassword) => {
    await api.post("/auth/reset-password", { token, newPassword });
  };

  const backToLoginFromReset = () => {
    // Strip ?reset_token= from the URL so refreshing doesn't reopen the reset screen.
    window.history.replaceState({}, "", window.location.pathname);
    setResetToken(null);
    setAuthScreen("login");
  };

  if (resetToken) {
    return (
      <div className="wcc-root min-h-screen">
        <style>{GLOBAL_CSS}</style>
        <ResetPasswordScreen token={resetToken} onResetPassword={handleResetPassword} onDone={backToLoginFromReset} />
      </div>
    );
  }

  if (!viewer) {
    if (authToken && !state.loaded) {
      return (
        <div className="wcc-root min-h-screen flex items-center justify-center" style={{ background: C.bg, color: C.text2 }}>
          <style>{GLOBAL_CSS}</style>
          <div className="text-sm">Loading TaskFlow…</div>
        </div>
      );
    }
    return (
      <div className="wcc-root min-h-screen">
        <style>{GLOBAL_CSS}</style>
        {authScreen === "forgot" ? (
          <ForgotPasswordScreen onRequestReset={handleRequestReset} onBackToLogin={() => setAuthScreen("login")} />
        ) : (
          <LoginScreen onSignIn={handleSignIn} loading={authLoading} error={authError} onForgotPassword={() => setAuthScreen("forgot")} />
        )}
      </div>
    );
  }

  const openTask = (id) => { setTaskDetailId(id); setNotifsOpen(false); };
  const openUser = (id) => setProfileCardUserId(id);
  const openProject = (id) => { setFocusProjectId(id); setScreen("projects"); };
  const closeTask = () => setTaskDetailId(null);
  const currentTask = state.tasks.find((t) => t.id === taskDetailId);
  const userNotifs = state.notifications.filter((n) => n.userId === viewer.id).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const unread = userNotifs.filter((n) => !n.read).length;

  const screenTitles = {
    dashboard: "Dashboard",
    my_tasks: "My Tasks",
    assigned_by_me: "Assigned by Me",
    help_requests: "Help Requests",
    team: "Team",
    reports: "Reports",
    users: "Users & Hierarchy",
    settings: "Settings",
  };

  // Every task action below updates local state instantly (optimistic UI, unchanged from the
  // original prototype) and persists to the API in the background. If the API call fails, we
  // toast an error — the next full reload will resync from the server as the source of truth.
  const syncFail = (label) => pushToast({ title: "Not saved", message: `${label} — will retry on next refresh.` });

  const handleAction = (action, payload = {}) => {
    if (!currentTask) return;
    const t = currentTask;
    switch (action) {
      case "start":
        dispatch({ type: "SET_STATUS", taskId: t.id, status: "in_progress", userId: viewer.id });
        pushToast({ title: "Task started", message: t.title });
        api.patch(`/tasks/${t.id}/status`, { status: "in_progress" }).catch(() => syncFail("Start task"));
        break;
      case "complete":
        dispatch({ type: "SET_STATUS", taskId: t.id, status: "completed", userId: viewer.id, details: `${viewer.name} marked task complete` });
        pushToast({ title: "Marked complete", message: t.title });
        api.patch(`/tasks/${t.id}/status`, { status: "completed" }).catch(() => syncFail("Complete task"));
        break;
      case "ask_help":
        setPickerModal({ mode: "ask_help", taskId: t.id });
        break;
      case "accept_help":
        dispatch({ type: "ACCEPT_HELP", taskId: t.id, userId: viewer.id });
        pushToast({ title: "You joined the loop", message: t.title });
        api.post(`/tasks/${t.id}/accept-help`).catch(() => syncFail("Accept help"));
        break;
      case "decline_help":
        dispatch({ type: "DECLINE_HELP", taskId: t.id, userId: viewer.id });
        pushToast({ title: "Help declined", message: t.title });
        api.post(`/tasks/${t.id}/decline-help`).catch(() => syncFail("Decline help"));
        break;
      case "complete_help":
        dispatch({ type: "COMPLETE_HELP", taskId: t.id, userId: viewer.id });
        pushToast({ title: "Help complete", message: `Back to ${(userMap[t.assignedTo]?.name || "Unknown user")}` });
        api.post(`/tasks/${t.id}/complete-help`).catch(() => syncFail("Complete help"));
        break;
      case "close_task":
        dispatch({ type: "SET_STATUS", taskId: t.id, status: "closed", userId: viewer.id, details: `${viewer.name} closed the task` });
        pushToast({ title: "Task closed", message: t.title });
        api.patch(`/tasks/${t.id}/status`, { status: "closed" }).catch(() => syncFail("Close task"));
        break;
      case "reassign":
        setPickerModal({ mode: "reassign", taskId: t.id });
        break;
      case "duplicate":
        api.post(`/tasks/${t.id}/duplicate`)
          .then(({ data }) => dispatch({ type: "DUPLICATE", serverTask: data }))
          .catch(() => syncFail("Duplicate task"));
        pushToast({ title: "Task duplicated", message: `Copy saved as draft` });
        break;
      case "delete_task": {
        // Permanent — confirm before doing anything.
        const sure = window.confirm(
          `Delete "${t.title}" permanently?\n\nThis removes the task and its comments, activity and reminders. This cannot be undone.`
        );
        if (!sure) break;
        api.delete(`/tasks/${t.id}`)
          .then(() => {
            dispatch({ type: "DELETE_TASK", taskId: t.id });
            pushToast({ title: "Task deleted", message: t.title });
          })
          .catch((err) => {
            const msg = err?.response?.data?.error || "Delete task";
            syncFail(msg);
          });
        break;
      }
      case "edit_title":
        dispatch({ type: "EDIT_TITLE", taskId: t.id, title: payload.title });
        api.patch(`/tasks/${t.id}/title`, { title: payload.title }).catch(() => syncFail("Rename task"));
        break;
      case "delete_reminder":
        dispatch({ type: "DELETE_REMINDER", reminderId: payload.reminderId });
        pushToast({ title: "Reminder removed", message: t.title });
        api.delete(`/tasks/${t.id}/reminders/${payload.reminderId}`).catch(() => syncFail("Delete reminder"));
        break;
      default: break;
    }
  };

  const handlePickerConfirm = (userId, note) => {
    if (!pickerModal) return;
    if (pickerModal.mode === "reassign") {
      dispatch({ type: "REASSIGN", taskId: pickerModal.taskId, newAssignee: userId, userId: viewer.id });
      pushToast({ title: "Reassigned", message: `${(userMap[userId]?.name || "Unknown user")} now owns this task` });
      api.patch(`/tasks/${pickerModal.taskId}/reassign`, { newAssignee: userId }).catch(() => syncFail("Reassign task"));
    }
    if (pickerModal.mode === "ask_help") {
      dispatch({ type: "ASK_HELP", taskId: pickerModal.taskId, helperId: userId, userId: viewer.id, note });
      pushToast({ title: "Help requested", message: `${(userMap[userId]?.name || "Unknown user")} was notified` });
      api.post(`/tasks/${pickerModal.taskId}/ask-help`, { helperId: userId, note }).catch(() => syncFail("Ask for help"));
    }
    setPickerModal(null);
  };

  // Throws on failure so NewUserModal can show the error inline instead of a toast.
  const handleCreateUser = async (payload) => {
    const { data: user } = await api.post("/users", payload);
    userMap[user.id] = user;
    dispatch({ type: "ADD_USER", user });
    pushToast({ title: "User created", message: `${user.name} can now sign in.` });
  };

  const handleUpdateUser = async (id, updates) => {
    const { data: user } = await api.patch(`/users/${id}`, updates);
    userMap[user.id] = user;
    dispatch({ type: "UPDATE_USER", user });
    pushToast({ title: "User updated", message: `${user.name}'s details were saved.` });
    return user;
  };

  const handleUploadAvatar = async (id, file) => {
    const formData = new FormData();
    formData.append("avatar", file);
    const token = localStorage.getItem("wcc_token");
    const { data: user } = await api.post(`/users/${id}/avatar`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    userMap[user.id] = user;
    dispatch({ type: "UPDATE_USER", user });
    pushToast({ title: "Profile picture updated", message: "" });
    return user;
  };

  const handleRemoveAvatar = async (id) => {
    const { data: user } = await api.delete(`/users/${id}/avatar`);
    userMap[user.id] = user;
    dispatch({ type: "UPDATE_USER", user });
    pushToast({ title: "Profile picture removed", message: "" });
    return user;
  };

  const handleDeleteUser = async (id) => {
    await api.delete(`/users/${id}`);
    dispatch({ type: "DELETE_USER", userId: id });
    pushToast({ title: "User deleted", message: "The account was removed." });
  };

  const handleSendReset = async (id) => {
    const { data } = await api.post(`/users/${id}/send-reset`);
    pushToast({ title: "Reset email sent", message: data.message || "The user can now set a new password." });
  };

  const handleCreateProject = async (payload) => {
    const { data: project } = await api.post("/projects", payload);
    dispatch({ type: "ADD_PROJECT", project });
    pushToast({ title: "Project created", message: project.name });
    return project;
  };

  const handleUpdateProject = async (id, updates) => {
    const { data: project } = await api.patch(`/projects/${id}`, updates);
    dispatch({ type: "UPDATE_PROJECT", project });
    pushToast({ title: "Project updated", message: project.name });
    return project;
  };

  const handleDeleteProject = async (id) => {
    await api.delete(`/projects/${id}`);
    dispatch({ type: "DELETE_PROJECT", projectId: id });
    pushToast({ title: "Project deleted", message: "Its tasks were kept and detached." });
  };

  const renderScreen = () => {
    switch (screen) {
      case "dashboard":
        if (canManageAll(viewer))
          return <DashboardDirector tasks={state.tasks} onOpenTask={openTask} />;
        if (roleRank[viewer.role] >= roleRank["Team Lead"])
          return <DashboardManager user={viewer} tasks={state.tasks} users={users} onOpenTask={openTask} helpRequests={state.helpRequests} />;
        return <DashboardEmployee user={viewer} tasks={state.tasks} activity={state.activity} onOpenTask={openTask} helpRequests={state.helpRequests} />;
      case "my_tasks":
        return <TaskTableScreen mode="my_tasks" user={viewer} tasks={state.tasks} users={users} onOpenTask={openTask} onNewTask={() => setNewTaskOpen(true)} />;
      case "assigned_by_me":
        return <TaskTableScreen mode="assigned_by_me" user={viewer} tasks={state.tasks} users={users} onOpenTask={openTask} onNewTask={() => setNewTaskOpen(true)} />;
      case "projects":
        return <ProjectsScreen projects={state.projects} tasks={state.tasks} users={users} viewer={viewer} onCreateProject={handleCreateProject} onUpdateProject={handleUpdateProject} onDeleteProject={handleDeleteProject} onOpenTask={openTask} focusProjectId={focusProjectId} onFocusHandled={() => setFocusProjectId(null)} />;
      case "help_requests":
        return <HelpRequestsScreen viewer={viewer} helpRequests={state.helpRequests} tasks={state.tasks} users={users} onOpenTask={openTask} />;
      case "team":
        return canSeeTeam(viewer)
          ? <TeamScreen viewer={viewer} users={users} tasks={state.tasks} onOpenTask={openTask} />
          : <EmptyState icon={ShieldCheck} title="Access restricted" subtitle="The Team view is available from Team Lead upwards." />;
      case "reports":
        return canSeeReports(viewer)
          ? <ReportsScreen tasks={state.tasks} users={users} />
          : <EmptyState icon={ShieldCheck} title="Access restricted" subtitle="Reports are available from Manager upwards." />;
      case "users":
        return <UsersScreen users={users} viewer={viewer} onCreateUser={handleCreateUser} onUpdateUser={handleUpdateUser} onDeleteUser={handleDeleteUser} onSendReset={handleSendReset} focusUserId={focusUserId} onFocusHandled={() => setFocusUserId(null)} />;
      case "settings":
        return <SettingsScreen viewer={viewer} onSignOut={handleSignOut} onUploadAvatar={handleUploadAvatar} onRemoveAvatar={handleRemoveAvatar} />;
      default:
        return null;
    }
  };

  return (
    <div className="wcc-root min-h-screen flex" style={{ background: C.bg }}>
      <style>{GLOBAL_CSS}</style>
      <Sidebar
        currentScreen={screen}
        setScreen={setScreen}
        user={viewer}
        onSignOut={handleSignOut}
        mobileOpen={mobileNavOpen}
        setMobileOpen={setMobileNavOpen}
      />
      <div className="flex-1 flex flex-col min-w-0" style={{ minHeight: "100vh" }}>
        <TopBar
          user={viewer}
          users={users}
          setCurrentUserId={setCurrentUserId}
          notifications={userNotifs}
          onOpenSearch={() => setCmdOpen(true)}
          onOpenNotifications={() => setNotifsOpen(!notifsOpen)}
          onOpenMobileNav={() => setMobileNavOpen(true)}
          unreadCount={unread}
          screenTitle={screenTitles[screen] || ""}
          onSignOut={handleSignOut}
        />
        <main className="flex-1 overflow-y-auto wcc-scroll wcc-fade" style={{ paddingBottom: 96 }} key={screen + viewer.id}>
          {renderScreen()}
        </main>
      </div>

      <TaskDetailDrawer
        open={!!currentTask}
        onClose={closeTask}
        task={currentTask}
        users={users}
        tasks={state.tasks}
        viewer={viewer}
        comments={state.comments}
        activity={state.activity}
        reminders={state.reminders}
        onAction={handleAction}
        onOpenTask={openTask}
        onCreateSubtask={(parentTask, title) => {
          const payload = {
            title,
            description: "",
            assignedTo: parentTask.assignedTo,
            priority: parentTask.priority,
            dueDate: parentTask.dueDate,
            category: parentTask.category,
            tags: [],
            status: "assigned",
            parentTaskId: parentTask.id,
          };
          api.post("/tasks", payload)
            .then(async ({ data: subtask }) => {
              dispatch({ type: "CREATE_TASK", data: payload, userId: viewer.id, serverTask: subtask });
              pushToast({ title: "Sub-task added", message: subtask.title });
            })
            .catch(() => syncFail("Add sub-task"));
        }}
        onAddComment={(taskId, text) => {
          api.post(`/tasks/${taskId}/comments`, { text })
            .then(({ data }) => dispatch({ type: "ADD_COMMENT", taskId, userId: viewer.id, text, serverComment: data }))
            .catch(() => syncFail("Add comment"));
        }}
        onAddReminder={(taskId, reminder) => {
          api.post(`/tasks/${taskId}/reminders`, reminder)
            .then(({ data }) => dispatch({ type: "ADD_REMINDER", taskId, reminder, userId: viewer.id, serverReminder: data }))
            .catch(() => syncFail("Add reminder"));
        }}
        onEditTask={async (taskId, updates) => {
          const { data } = await api.patch(`/tasks/${taskId}`, updates);
          dispatch({ type: "EDIT_TASK", taskId, userId: viewer.id, updates: data });
          pushToast({ title: "Task updated", message: currentTask?.title || "" });
        }}
      />

      <NewTaskModal
        projects={state.projects}
        open={newTaskOpen}
        onClose={() => setNewTaskOpen(false)}
        onCreate={(data) => {
          return api.post("/tasks", data)
            .then(async ({ data: task }) => {
              const { data: reminders } = await api.get(`/tasks/${task.id}/reminders`);
              dispatch({ type: "CREATE_TASK", data, userId: viewer.id, serverTask: task, serverReminders: reminders });
              pushToast({ title: "Task created", message: task.title });
              return task;
            })
            .catch((err) => { syncFail("Create task"); throw err; });
        }}
        users={users}
        viewerId={viewer.id}
      />

      <UserPickerModal
        open={!!pickerModal}
        onClose={() => setPickerModal(null)}
        onPick={handlePickerConfirm}
        title={pickerModal?.mode === "reassign" ? "Reassign task" : "Ask for help"}
        subtitle={pickerModal?.mode === "reassign" ? "Choose who should own this task next." : "Pick a colleague to bring into the loop."}
        users={users}
        excludeIds={pickerModal ? [state.tasks.find((t) => t.id === pickerModal.taskId)?.assignedTo] : []}
        initialNote={pickerModal?.mode === "ask_help"}
      />

      <CommandPalette
        open={cmdOpen}
        onClose={() => setCmdOpen(false)}
        tasks={state.tasks}
        users={users}
        projects={state.projects}
        onOpenTask={openTask}
        onOpenUser={openUser}
        onOpenProject={openProject}
        onNavigate={setScreen}
      />

      <NotificationsPanel
        open={notifsOpen}
        onClose={() => setNotifsOpen(false)}
        notifications={userNotifs}
        users={users}
        onOpenTask={(taskId) => { setNotifsOpen(false); openTask(taskId); }}
        markAllRead={() => {
          dispatch({ type: "MARK_ALL_NOTIFS_READ", userId: viewer.id });
          api.patch("/notifications/mark-all-read").catch(() => syncFail("Mark notifications read"));
        }}
      />

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      <ProfileCardModal userId={profileCardUserId} onClose={() => setProfileCardUserId(null)} />
    </div>
  );
}